import React, {
  useState,
  Fragment,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  Box,
  Typography,
  TableCell,
  TableRow,
  IconButton,
  MenuItem,
  Select as MuiSelect,
  Tooltip,
  LinearProgress,
  Stack,
} from "@mui/material";
// import DownloadIcon from "@mui/icons-material/Download"; // TODO: CSV出力実装後にコメント解除
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  CustomButton,
  CustomSingleSelect,
  CustomCategorySelectSingle,
  CustomSectionTitle,
  CustomTruncateText,
  CustomSummaryCard,
  CustomCsvUploadModal,
  CustomModal,
  CustomCheckbox,
  CustomTextInput,
  colors,
  type CsvValidationResult,
} from "@ippon/shared-components";
import { formatSignedYen, formatYen } from "@ippon/shared-utils";
import DescriptionIcon from "@mui/icons-material/Description";
import {
  parseBudgetCsv,
  validateBudgetCsv,
  serializeBudgetCsv,
  serializeEmptyTemplate,
  buildCsvSummary,
  type SerializableBudgetEntry,
  type ExistingCategory,
  type CategoryGroup,
  type NewCategoryCandidate,
} from "@/utils/budgetCsv";
import ExpenseCreateDrawer from "../ExpenseManagement/ExpenseCreateDrawer";
import ExpenseDetailDrawer from "../ExpenseManagement/ExpenseDetailDrawer";
// 「収入追加」は Unit 登録モーダル。
// 「区画プレビュー」は復元した PrivateAreaDetailDrawer (PrivateAreaDetail を embedded 表示) を使う。
import UnitCreateModal from "../Unit/UnitCreateModal";
import IncomeCreateDrawer from "../shared/IncomeCreateDrawer";
import PaymentDetailDrawer from "../shared/PaymentDetailDrawer";
import PrivateAreaDetailDrawer from "./PrivateAreaDetailDrawer";
import { DropDownArrowIcon, RequireIcon } from "@ippon/shared-assets";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/store";
import {
  fetchBudgets,
  fetchBudgetCategories,
  createBudgetCategory,
  syncRentRoll,
  createBudget,
  updateBudget,
  updateBudgetCategoryCell,
  updateBudgetDetailManualActual,
  bulkUploadBudgets,
  type BudgetRecord,
  type BudgetMonthData,
  type BulkBudgetRow,
} from "@/store/budgetSlice";
import { fetchPaymentsByProperty } from "@/store/paymentSlice";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { Can } from "@/components/auth/Can";
import InitialLoadError from "@/components/ErrorState/InitialLoadError";
import type { ApiErrorPayload, ApiErrorType } from "@/utils/apiErrorPayload";
import { buildFiscalYearOptions } from "@/utils/fiscalYearOptions";
import {
  shouldShowInitialLoadError,
  useStaleInitialLoadErrorToast,
} from "@/utils/initialLoadError";

// 型・純粋ヘルパー・定数は純リファクタで別ファイルへ切り出した (挙動不変)。
import type {
  BudgetDetailRow,
  BudgetGroup,
  BudgetRow,
  BudgetProps,
  BudgetFormValues,
} from "./budgetTypes";
import { VIEW_MODE_OPTIONS } from "./budgetTypes";
import {
  getOrderedMonthKeys,
  getMonthLabels,
  canEditCell,
  canEditBudgetDetailCell,
  isExpenseBudgetGroup,
  subtractWhenBothEntered,
  apiRecordToBudgetRow,
  buildEmptyMonths,
} from "./budgetHelpers";
import BudgetTableVirtual from "./BudgetTableVirtual";
import type {
  RowVM,
  RowVMCell,
  AddAction,
  LinkAction,
} from "./BudgetTableVirtual";

export const Budget: React.FC<BudgetProps> = ({ propertyId, propertyName }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();
  const { list, categories } = useSelector((state: RootState) => state.budgets);
  const [budgetTableLoading, setBudgetTableLoading] = useState(false);
  const budgetFetchSeqRef = useRef(0);
  // BE は決算月 (fiscal_closing_month) を返す。会計年度開始月はその翌月。
  const fiscalClosingMonth: number =
    useSelector(
      (state: RootState) => state.clients?.detailed?.data?.fiscal_closing_month,
    ) ?? 3;
  const fiscalYearStartMonth: number =
    fiscalClosingMonth === 12 ? 1 : fiscalClosingMonth + 1;

  // 現在の会計年度起算年 (例: 3月決算で 2026/02 → 2025年度)
  const currentFiscalYear = useMemo(() => {
    const now = new Date();
    return now.getMonth() + 1 >= fiscalYearStartMonth
      ? now.getFullYear()
      : now.getFullYear() - 1;
  }, [fiscalYearStartMonth]);

  const FISCAL_YEAR_OPTIONS = useMemo(
    () => buildFiscalYearOptions(currentFiscalYear),
    [currentFiscalYear],
  );

  const [fiscalYear, setFiscalYear] = useState(String(currentFiscalYear));
  const [initialLoadError, setInitialLoadError] = useState<ApiErrorType | null>(
    null,
  );
  // fiscal_closing_month が非同期で取れた直後の 1 回だけ defaultYear に追従
  const fiscalYearInitSyncedRef = useRef(false);
  useEffect(() => {
    if (!fiscalYearInitSyncedRef.current) {
      setFiscalYear(String(currentFiscalYear));
      fiscalYearInitSyncedRef.current = true;
    }
  }, [currentFiscalYear]);
  const [viewMode, setViewMode] = useState<"monthly" | "quarterly">("monthly");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [groupExpanded, setGroupExpanded] = useState<Record<string, boolean>>({
    income: false,
    expense: false,
    capex: false,
    loan: false,
  });
  // CSV アップロード/エクスポート モーダル
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [csvSubmitting, setCsvSubmitting] = useState(false);

  // インライン追加用 state（支出）
  const [addingExpense, setAddingExpense] = useState(false);
  const [newExpenseCategoryId, setNewExpenseCategoryId] = useState("");
  const [addingSubmitting, setAddingSubmitting] = useState(false);

  // インライン追加用 state（収入）
  const [addingIncome, setAddingIncome] = useState(false);
  const [newIncomeCategoryId, setNewIncomeCategoryId] = useState("");
  const [addingIncomeSubmitting, setAddingIncomeSubmitting] = useState(false);

  // 科目追加 (新方式 §3.3): どのグループ/サブグループの追加行を開いているか。
  //   収入: "income:stock_rent" / "income:stock_other" / "income:other"
  //   支出: "expense_opex" / "expense_capex"
  const [addCategoryFor, setAddCategoryFor] = useState<string | null>(null);
  const [addCategorySubmitting, setAddCategorySubmitting] = useState(false);
  // 二重発火ガード。CustomCategorySelectSingle は新規追加時に onAddNewOption と
  // onValueChange を同一 tick で連続発火するため、state (非同期更新) では二重 POST を
  // 防げない。最初の await より前に同期で立てる ref で 2 回目の呼び出しを確実に弾く。
  const addCategoryInFlightRef = useRef(false);

  // 実績 (ExpenseRecord) 追加 drawer の state
  // 予実 actual は必ず ExpenseRecord 経由で反映するため、行の
  // 「+ 支出追加」からこの drawer を開いて Expense を登録する。
  const [expenseDrawer, setExpenseDrawer] = useState<{
    open: boolean;
    rowCategory: string;
    rowCategoryId?: string;
    rowGroup: BudgetGroup;
  }>({ open: false, rowCategory: "", rowGroup: "expense" });
  // 支出詳細プレビュードロワー
  const [previewExpenseId, setPreviewExpenseId] = useState<string | null>(null);
  // 区画詳細プレビュードロワー（展開先は /properties/:id/units/:unitId）
  const [previewSectionId, setPreviewSectionId] = useState<string | null>(null);

  // 「+収入追加」モーダル状態
  // - "private_area": 専有部登録モーダル (賃料/共益費カテゴリ用)
  // - "ancillary": 付帯収益用 Unit 登録モーダル (area_type 未指定でユーザー選択)
  const [incomeAddModal, setIncomeAddModal] = useState<
    { kind: "none" } | { kind: "private_area" } | { kind: "ancillary" }
  >({ kind: "none" });
  // 「その他」(revenue_group=other) 配下の「収入追加」: 契約(Unit)非紐付け収入を
  // 収入タブと同じ収入登録モーダル (PaymentRecord 直登録) で登録する。
  const [incomePaymentOpen, setIncomePaymentOpen] = useState(false);
  // 「その他」入金行クリックで開く収入詳細ドロワー (対象の生 PaymentRecord)
  const [incomeDetailPayment, setIncomeDetailPayment] = useState<any | null>(
    null,
  );

  const { control, reset: resetForm } = useForm<BudgetFormValues>({
    defaultValues: {
      fiscalYear: String(currentFiscalYear),
      viewMode: "monthly",
    },
  });
  // fiscal_closing_month が非同期で取れた初回 1 度だけ form の defaultValues も同期
  const formInitSyncedRef = useRef(false);
  useEffect(() => {
    if (!formInitSyncedRef.current) {
      resetForm({
        fiscalYear: String(currentFiscalYear),
        viewMode: "monthly",
      });
      formInitSyncedRef.current = true;
    }
  }, [currentFiscalYear, resetForm]);

  const orderedKeys = useMemo(
    () => getOrderedMonthKeys(fiscalYearStartMonth),
    [fiscalYearStartMonth],
  );
  const MONTHS = useMemo(
    () => getMonthLabels(fiscalYearStartMonth),
    [fiscalYearStartMonth],
  );

  const resolveBudgetRowId = useCallback(
    (category: { category_id: string; category_type: string }): string => {
      return `${propertyId}#${fiscalYear}#${category.category_id}`;
    },
    [propertyId, fiscalYear],
  );

  // 「その他」入金 (Unit 非紐付け) を redux にロード (他画面・収入詳細ドロワーが参照)。
  // 予実表では非紐付け入金も予実カテゴリレコードの actual に集約済みのため、
  // ここで 1 件=1 行へ変換して表示することはしない (科目行で表示する)。
  useEffect(() => {
    if (propertyId) {
      dispatch(fetchPaymentsByProperty({ propertyId }) as any);
    }
  }, [dispatch, propertyId]);

  // ── CSV: BudgetCategoryType → CSV 大カテゴリ ──
  const mapCategoryGroup = useCallback(
    (categoryType: string): CategoryGroup =>
      categoryType === "income" ? "収入" : "支出",
    [],
  );

  // ── CSV: 既存科目マスタを CSV バリデータの形式に変換 ──
  // 段階2 (科目テーブル化): system 科目に加え、ユーザー追加科目 (「その他」収入等) も
  // 検証対象にする。categories.data はサーバ (listBudgetCategories) が既に
  // 廃止旧system / 削除済みを除外済みなので、ここでは全件を有効科目として扱う。
  const csvExistingCategories = useMemo<ExistingCategory[]>(() => {
    const cats = (categories?.data ?? []) as Array<{
      name: string;
      category_type: string;
      revenue_group?: string;
      expense_group?: string;
      level?: "category" | "item";
      is_system?: boolean;
      deleted_at?: string;
    }>;
    const rgLabel = (rg?: string) =>
      rg === "stock_rent"
        ? "貸室"
        : rg === "stock_other"
          ? "付帯"
          : rg === "other"
            ? "その他"
            : (rg ?? undefined);
    return cats
      .filter(
        (c) =>
          !c.deleted_at &&
          (c.category_type === "income" || c.level !== "category"),
      )
      .map((c) => ({
        name: c.name,
        group: mapCategoryGroup(c.category_type),
        // カテゴリ列照合用: 収入=revenue_group の表示名、支出=expense_group。
        categoryGroup:
          c.category_type === "income"
            ? rgLabel(c.revenue_group)
            : c.expense_group,
      }));
  }, [categories, mapCategoryGroup]);

  // category_id → 現マスタ name の解決マップ。
  // BudgetRecord.category は作成時点の snapshot で、マスタ rename 後はずれる。
  // CSV シリアライズ時は category_id から現マスタ name を引き直し、
  // ダウンロード→再アップロード roundtrip を保証する (impl-bugs.md IB-011)。
  const categoryNameById = useMemo<Map<string, string>>(() => {
    const m = new Map<string, string>();
    const cats = (categories?.data ?? []) as Array<{
      category_id: string;
      name: string;
    }>;
    for (const c of cats) m.set(c.category_id, c.name);
    return m;
  }, [categories]);

  // category_id → revenue_group (収入の 2 階層目グルーピング用。§3.1)
  const revenueGroupById = useMemo<Map<string, string>>(() => {
    const m = new Map<string, string>();
    const cats = (categories?.data ?? []) as Array<{
      category_id: string;
      revenue_group?: string;
    }>;
    for (const c of cats) {
      if (c.revenue_group) m.set(c.category_id, c.revenue_group);
    }
    return m;
  }, [categories]);

  // category_id → expense_group (支出の上位カテゴリ。revenueGroupById の支出版)
  const expenseGroupById = useMemo<Map<string, string>>(() => {
    const m = new Map<string, string>();
    const cats = (categories?.data ?? []) as Array<{
      category_id: string;
      expense_group?: string;
    }>;
    for (const c of cats) {
      if (c.expense_group) m.set(c.category_id, c.expense_group);
    }
    return m;
  }, [categories]);

  // category_id → level (親子モデル 2026-07)。
  // "category"=カテゴリ(親): 予実では expense_group の見出し(サブグループ)を作る
  //   ためだけに使い、明細行としては描画しない (空カテゴリ表示 & 2重回避)。
  // "item"/未設定=明細科目(子): 従来どおり明細行として描画。
  const categoryLevelById = useMemo<Map<string, string>>(() => {
    const m = new Map<string, string>();
    const cats = (categories?.data ?? []) as Array<{
      category_id: string;
      level?: string;
    }>;
    for (const c of cats) {
      if (c.level) m.set(c.category_id, c.level);
    }
    return m;
  }, [categories]);

  // ── CSV: BudgetRecord[] → SerializableBudgetEntry[] (現在のデータ DL 用) ──
  // 予実表と同じ現存カテゴリ条件で絞り込み、orphan のレガシー行は
  // CSV ダウンロードに含めない (download → upload roundtrip を保証する)。
  // 並び順は予実表と一致させる:
  //   1. 収入 (income) → 支出 (expense_*) → 融資 (loan)
  //   2. 同グループ内は category.display_order 昇順
  const csvSerializableEntries = useMemo<SerializableBudgetEntry[]>(() => {
    const knownCategoryIds = new Set(categories.data.map((c) => c.category_id));
    const displayOrderById = new Map<string, number>();
    for (const c of categories.data) {
      displayOrderById.set(c.category_id, c.display_order ?? 9999);
    }
    const groupRank = (ct: string): number => {
      if (ct === "income") return 0;
      if (ct === "expense_opex" || ct === "expense") return 1;
      if (ct === "expense_capex") return 2;
      if (ct === "loan") return 3;
      return 9;
    };
    const records = ((list?.data ?? []) as BudgetRecord[])
      .filter((r) => knownCategoryIds.has(r.category_id))
      // カテゴリ(親, level="category")は明細科目ではないので CSV には出さない。
      .filter((r) => categoryLevelById.get(r.category_id) !== "category")
      .slice()
      .sort((a, b) => {
        const ga = groupRank(a.category_type);
        const gb = groupRank(b.category_type);
        if (ga !== gb) return ga - gb;
        const oa = displayOrderById.get(a.category_id) ?? 9999;
        const ob = displayOrderById.get(b.category_id) ?? 9999;
        return oa - ob;
      });
    return records.map((r) => {
      const budgetMonths: Record<string, number | null> = {};
      for (const k of orderedKeys) {
        const m = r.months?.[k];
        budgetMonths[k] = m && typeof m.budget === "number" ? m.budget : null;
      }
      // 実績は科目単位 (詳細(区画)内訳は廃止)。months[].actual をそのまま出力。
      const actualMonths: Record<string, number | null> = {};
      for (const k of orderedKeys) {
        const m = r.months?.[k];
        actualMonths[k] = m && typeof m.actual === "number" ? m.actual : null;
      }
      // category_id から現マスタ name を引く。マスタに無ければ snapshot にフォールバック。
      const currentName = categoryNameById.get(r.category_id) ?? r.category;
      const isIncome = r.category_type === "income";
      // カテゴリ列の値: 収入=revenue_group の表示名(貸室/付帯/その他)、支出=expense_group。
      const rgLabel = (rg?: string) =>
        rg === "stock_rent"
          ? "貸室"
          : rg === "stock_other"
            ? "付帯"
            : rg === "other"
              ? "その他"
              : (rg ?? "");
      const categoryGroup = isIncome
        ? rgLabel(revenueGroupById.get(r.category_id))
        : (expenseGroupById.get(r.category_id) ?? "");
      return {
        fiscalYear: r.fiscal_year,
        group: mapCategoryGroup(r.category_type),
        categoryGroup,
        category: currentName,
        budgetMonths,
        actualMonths,
      };
    });
  }, [
    list,
    orderedKeys,
    mapCategoryGroup,
    categoryNameById,
    categories.data,
    revenueGroupById,
    expenseGroupById,
  ]);

  // ── CSV: ブラウザダウンロード ──
  const triggerCsvDownload = useCallback(
    (content: string, fileName: string) => {
      const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    [],
  );

  const handleDownloadEmptyTemplate = useCallback(() => {
    const csv = serializeEmptyTemplate({ startMonth: fiscalYearStartMonth });
    triggerCsvDownload(csv, "予実_テンプレート.csv");
  }, [fiscalYearStartMonth, triggerCsvDownload]);

  const handleDownloadCurrent = useCallback(() => {
    const csv = serializeBudgetCsv(csvSerializableEntries, {
      startMonth: fiscalYearStartMonth,
    });
    triggerCsvDownload(csv, `予実_${fiscalYear}年度.csv`);
  }, [
    csvSerializableEntries,
    fiscalYear,
    fiscalYearStartMonth,
    triggerCsvDownload,
  ]);

  // パース成功時の rows を確定アップロード時に再利用するためのキャッシュ
  const parsedRowsRef = useRef<BulkBudgetRow[]>([]);
  const csvNewExpenseCategoriesRef = useRef<NewCategoryCandidate[]>([]);

  const buildNewExpenseCategoryItems = useCallback(
    (items: NewCategoryCandidate[]) => {
      const grouped = new Map<string, string[]>();
      for (const item of items) {
        if (item.group !== "支出") continue;
        const category = item.categoryGroup.trim();
        const name = item.name.trim();
        if (!category || !name) continue;
        const names = grouped.get(category) ?? [];
        if (!names.includes(name)) names.push(name);
        grouped.set(category, names);
      }
      return Array.from(grouped.entries()).map(([category, itemNames]) => ({
        category,
        itemNames,
      }));
    },
    [],
  );

  // ── CSV: パース + バリデーション (モーダルが呼ぶ) ──
  const handleParseAndValidate = useCallback(
    async (file: File): Promise<CsvValidationResult> => {
      parsedRowsRef.current = [];
      csvNewExpenseCategoriesRef.current = [];
      const text = await file.text();
      const parsed = parseBudgetCsv(text, { startMonth: fiscalYearStartMonth });
      if (!parsed.ok) {
        return {
          isValid: false,
          rowCount: 0,
          errors: parsed.errors.map((e) => ({
            line: e.lineNumber,
            message: e.message,
          })),
          warnings: [],
          preview: [],
        };
      }
      const v = validateBudgetCsv(parsed.rows, {
        existingCategories: csvExistingCategories,
      });
      const preview: string[][] = [];
      const monthLabels = parsed.monthOrder.map((k) => `${parseInt(k, 10)}月`);
      preview.push([
        "年度",
        "収入/支出",
        "カテゴリ",
        "明細項目",
        "予算/実績",
        ...monthLabels,
      ]);
      for (const row of parsed.rows.slice(0, 5)) {
        preview.push([
          row.fiscalYear,
          row.group,
          row.categoryGroup,
          row.category,
          row.rowKind,
          ...parsed.monthOrder.map((k) =>
            row.months[k] === null ? "" : String(row.months[k]),
          ),
        ]);
      }
      // 検証エラーが無いときだけ確定用にキャッシュ
      if (v.errors.length === 0) {
        parsedRowsRef.current = parsed.rows.map((r) => ({
          fiscal_year: r.fiscalYear,
          group: r.group,
          category_group: r.categoryGroup,
          category: r.category,
          row_kind: r.rowKind,
          months: r.months,
        }));
        csvNewExpenseCategoriesRef.current = v.newCategories.filter(
          (c) => c.group === "支出",
        );
      }
      // ── アップロード確認画面用サマリ ──
      const validNewCategories = v.errors.length === 0 ? v.newCategories : [];
      const newExpenseCategoryItems =
        buildNewExpenseCategoryItems(validNewCategories);
      const summary = buildCsvSummary({
        rows: parsed.rows,
        existingEntries: csvSerializableEntries,
        newCategoryNames: validNewCategories.map((c) => c.name),
        newExpenseCategoryItems,
      });
      return {
        isValid: v.errors.length === 0,
        rowCount: parsed.rows.length,
        errors: v.errors.map((e) => ({
          line: e.lineNumber,
          message: e.message,
        })),
        warnings: v.warnings.map((w) => ({
          line: w.lineNumber,
          message: w.message,
        })),
        notices: v.notices.map((n) => ({
          line: n.lineNumber,
          message: n.message,
        })),
        preview,
        summary,
      };
    },
    [
      buildNewExpenseCategoryItems,
      csvExistingCategories,
      csvSerializableEntries,
      fiscalYearStartMonth,
    ],
  );

  // ── CSV: アップロード確定 → BE bulk API ──
  const handleConfirmCsvUpload = useCallback(
    async (_file: File) => {
      const rows = parsedRowsRef.current;
      if (rows.length === 0) {
        showToast({
          message: "アップロード可能な行がありません",
          type: "error",
        });
        return;
      }
      setCsvSubmitting(true);
      try {
        const newExpenseCategories = csvNewExpenseCategoriesRef.current;
        if (newExpenseCategories.length > 0) {
          const maxDisplayOrder = Math.max(
            0,
            ...categories.data.map((c) => c.display_order ?? 0),
          );
          const parentCategoryIdByName = new Map<string, string>();
          for (const category of categories.data) {
            if (category.level !== "category") continue;
            parentCategoryIdByName.set(category.name, category.category_id);
          }

          let nextDisplayOrder = maxDisplayOrder + 1;
          for (const item of newExpenseCategories) {
            let parentCategoryId = parentCategoryIdByName.get(
              item.categoryGroup,
            );

            if (!parentCategoryId) {
              const parentResult = await dispatch(
                createBudgetCategory({
                  name: item.categoryGroup,
                  category_type: "expense_opex",
                  level: "category",
                  expense_group: item.categoryGroup,
                  display_order: nextDisplayOrder++,
                }),
              );
              if (!createBudgetCategory.fulfilled.match(parentResult)) {
                throw new Error(
                  parentResult.payload?.message ??
                    `カテゴリ「${item.categoryGroup}」の登録に失敗しました`,
                );
              }
              parentCategoryId = parentResult.payload.category_id;
              parentCategoryIdByName.set(item.categoryGroup, parentCategoryId);
            }

            const itemResult = await dispatch(
              createBudgetCategory({
                name: item.name,
                category_type: "expense_opex",
                level: "item",
                parent_category_id: parentCategoryId,
                display_order: nextDisplayOrder++,
              }),
            );
            if (!createBudgetCategory.fulfilled.match(itemResult)) {
              throw new Error(
                itemResult.payload?.message ??
                  `明細項目「${item.name}」の登録に失敗しました`,
              );
            }
          }
        }

        await dispatch(
          bulkUploadBudgets({
            property_id: propertyId,
            fiscal_year: fiscalYear,
            mode: "merge",
            rows,
          }),
        ).unwrap();
        // 取込み後はマスタ・予実データを再取得
        await dispatch(fetchBudgetCategories());
        await dispatch(
          fetchBudgets({ property_id: propertyId, fiscal_year: fiscalYear }),
        );
        showToast({
          message: "CSV を取込みました",
          type: "success",
        });
        setCsvModalOpen(false);
      } catch (e) {
        const msg =
          (e as { message?: string })?.message ||
          (typeof e === "string" ? e : "アップロードに失敗しました");
        showToast({ message: msg, type: "error" });
      } finally {
        setCsvSubmitting(false);
      }
    },
    [categories.data, dispatch, fiscalYear, propertyId, showToast],
  );

  const fetchDetail = useCallback(async () => {
    if (!propertyId || !fiscalYear) return;
    const requestSeq = budgetFetchSeqRef.current + 1;
    budgetFetchSeqRef.current = requestSeq;
    setBudgetTableLoading(true);
    setInitialLoadError(null);
    try {
      await dispatch(fetchBudgetCategories()).unwrap();
      try {
        await dispatch(
          syncRentRoll({ property_id: propertyId, fiscal_year: fiscalYear }),
        ).unwrap();
      } catch {
        // 初期表示は予実本体の取得結果を優先し、同期の transient error で toast を重ねない。
      }
      await dispatch(
        fetchBudgets({ property_id: propertyId, fiscal_year: fiscalYear }),
      ).unwrap();
    } catch (err) {
      const payload = err as ApiErrorPayload | undefined;
      setInitialLoadError(payload?.errorType ?? "unknown");
    } finally {
      if (budgetFetchSeqRef.current === requestSeq) {
        setBudgetTableLoading(false);
      }
    }
  }, [dispatch, propertyId, fiscalYear]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const data: BudgetRow[] = useMemo(() => {
    // 段階2 (科目テーブル化): ユーザー追加科目 (is_system=false) も予実表に表示する。
    // 予実レコードがまだ無い物件でも、クライアント共通マスタの科目は空行として表示する。
    const activeCategories = categories.data.filter((c: any) => !c.deleted_at);
    const knownCategoryIds = new Set(
      activeCategories.map((c) => c.category_id),
    );
    const rows = list.data
      .filter((r) => knownCategoryIds.has(r.category_id))
      .map((r) => apiRecordToBudgetRow(r, orderedKeys));
    const existingRowIds = new Set(rows.map((r) => r.id));
    for (const c of activeCategories) {
      const rowId = resolveBudgetRowId(c);
      if (existingRowIds.has(rowId)) continue;
      rows.push({
        id: rowId,
        category: c.name,
        categoryId: c.category_id,
        group: c.category_type,
        months: orderedKeys.map(() => ({ budget: null, actual: null })),
      });
    }
    return rows;
  }, [list.data, categories.data, orderedKeys, resolveBudgetRowId]);

  // ── セル編集 state ──
  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");
  // 手動値と積み上げ(明細合計)が食い違う (バッティング) セルの警告アイコン押下で開く確認モーダル。
  const [conflictModal, setConflictModal] = useState<{
    sortKey: string;
    colIdx: number;
    field: "budget" | "actual";
    manual: number | null;
    accumulated: number | null;
    choice: "manual" | "accumulated";
    /** セット時は「内訳(項目)の手動実績 vs 入金管理」モード。accumulated=入金管理額。 */
    detailLabel?: string;
  } | null>(null);
  // #4: モーダル内の金額入力 (CustomTextInput showYen) 用の RHF フォーム。
  const conflictForm = useForm<{ manualAmount: string; accumAmount: string }>({
    defaultValues: { manualAmount: "", accumAmount: "" },
  });

  const makeCellKey = (
    sortKey: string,
    monthIdx: number,
    field: "budget" | "actual",
    detailId?: string,
  ) =>
    detailId
      ? `${sortKey}__d__${detailId}__${monthIdx}__${field}`
      : `${sortKey}__${monthIdx}__${field}`;

  // 数値文字列を 3 桁カンマ区切りに整形 (非数字は除去)
  const formatWithCommas = (raw: string): string => {
    const digits = raw.replace(/[^\d-]/g, "");
    if (digits === "" || digits === "-") return digits;
    const num = Number(digits);
    if (isNaN(num)) return "";
    return num.toLocaleString("ja-JP");
  };

  const startCellEdit = (
    sortKey: string,
    monthIdx: number,
    field: "budget" | "actual",
    currentValue: number | null,
    detailId?: string,
  ) => {
    setEditingCell(makeCellKey(sortKey, monthIdx, field, detailId));
    // null (未入力) / 0 (明示ゼロ) はどちらも空欄から編集開始 (§7-I)
    setEditingValue(
      currentValue === null || currentValue === 0
        ? ""
        : currentValue.toLocaleString("ja-JP"),
    );
  };

  const cancelCellEdit = () => {
    setEditingCell(null);
    setEditingValue("");
  };

  /**
   * セル編集値を解釈する (§7-I)。
   * - 空欄 → null (セルをクリア = 未入力に戻す)
   * - 数値 → その値 (0 を含む。明示ゼロ)
   * - 不正 (NaN) → "invalid" (呼び出し側でエラー表示し中断)
   */
  const parseEditValue = (): number | null | "invalid" => {
    const trimmed = editingValue.trim();
    if (trimmed === "") return null; // クリア
    const numVal = Number(trimmed.replace(/,/g, ""));
    return isNaN(numVal) ? "invalid" : numVal;
  };

  const executeDetailCellUpdate = useCallback(
    async (update: {
      sortKey: string;
      detailId: string;
      monthKey: string;
      field: "budget" | "actual";
      amount: number;
      categoryTotalOverride?: number;
    }) => {
      const {
        sortKey,
        detailId,
        monthKey,
        field,
        amount,
        categoryTotalOverride,
      } = update;
      const record = list.data.find((r) => r.sort_key === sortKey);
      if (!record || !record.details) {
        showToast({
          message: "対象の予算レコードが見つかりません",
          type: "error",
        });
        cancelCellEdit();
        return;
      }

      // 詳細行の actual 編集 → 収入・支出とも 予実の手動実績(manual_actual) を更新。
      // 入金管理(PaymentRecord)/支出管理(ExpenseRecord) は触らない (分離・自動連携なし)。突合は名称(label)。
      if (field === "actual") {
        try {
          const targetDetail = record.details.find(
            (d) => (d.section_id || d.label) === detailId,
          );
          const label = targetDetail?.label;
          if (!label) {
            showToast({
              message: "詳細の名称が取得できないため更新できません。",
              type: "error",
            });
            cancelCellEdit();
            return;
          }
          await dispatch(
            updateBudgetDetailManualActual({
              property_id: propertyId,
              fiscal_year: String(fiscalYear),
              category_id: record.category_id,
              category_type: record.category_type,
              detail_label: label,
              month: monthKey,
              value: amount,
              // インライン入力は未解決 (入金と食い違えば！を出す)。
              actual_source: "auto",
            }),
          ).unwrap();
          if (categoryTotalOverride !== undefined) {
            await dispatch(
              updateBudgetCategoryCell({
                property_id: record.property_id,
                fiscal_year: record.fiscal_year,
                category_id: record.category_id,
                category_type: record.category_type,
                month: monthKey,
                field,
                amount: categoryTotalOverride,
              }),
            ).unwrap();
          }
          await dispatch(
            fetchBudgets({
              property_id: propertyId,
              fiscal_year: String(fiscalYear),
            }),
          );
        } catch (err: any) {
          const msg =
            err?.response?.data?.message || "実績の更新に失敗しました";
          showToast({ message: msg, type: "error" });
        }
        cancelCellEdit();
        return;
      }

      // 予算 (budget) は detail のみ更新。親カテゴリの予算は独立して扱うため
      // 親の months は触らない (= カテゴリ予算と詳細予算は完全別物)。
      const updatedDetails = record.details.map((d) => {
        const dId = d.section_id || d.label;
        if (dId !== detailId) return { ...d };
        const updatedMonths: Record<string, BudgetMonthData> = {};
        for (const k of orderedKeys) {
          updatedMonths[k] = d.months?.[k]
            ? { ...d.months[k] }
            : { budget: 0, actual: 0 };
        }
        updatedMonths[monthKey] = {
          ...updatedMonths[monthKey],
          [field]: amount,
        };
        return { ...d, months: updatedMonths };
      });
      const updatedMonths =
        categoryTotalOverride === undefined
          ? undefined
          : {
              ...(record.months || {}),
              [monthKey]: {
                ...(record.months?.[monthKey] || { budget: 0, actual: 0 }),
                [field]: categoryTotalOverride,
                manual_override: {
                  ...(record.months?.[monthKey]?.manual_override || {}),
                  [field]: true,
                },
              },
            };

      try {
        await dispatch(
          updateBudget({
            sort_key: sortKey,
            ...(updatedMonths ? { months: updatedMonths } : {}),
            details: updatedDetails,
            manual_override: true,
          }),
        ).unwrap();
      } catch {
        showToast({ message: "更新に失敗しました", type: "error" });
      }
      cancelCellEdit();
    },
    [dispatch, list.data, orderedKeys, showToast, propertyId, fiscalYear],
  );

  const commitCellEdit = useCallback(
    async (sortKey: string, monthIdx: number, field: "budget" | "actual") => {
      const record = list.data.find((r) => r.sort_key === sortKey);
      const category = categories.data.find((c: any) => {
        return !c.deleted_at && resolveBudgetRowId(c) === sortKey;
      });
      if (!record && !category) {
        showToast({
          message: "対象の予算レコードが見つかりません",
          type: "error",
        });
        cancelCellEdit();
        return;
      }

      const parsed = parseEditValue();
      if (parsed === "invalid") {
        showToast({ message: "数値を入力してください", type: "error" });
        cancelCellEdit();
        return;
      }
      // null = セルをクリア (未入力に戻す)。数値 = 明示値 (0 含む)。§7-I
      const numVal: number | null = parsed;

      const monthKey = orderedKeys[monthIdx];
      if (!monthKey) {
        showToast({ message: "対象月の特定に失敗しました", type: "error" });
        cancelCellEdit();
        return;
      }

      const currentMonthData = record?.months?.[monthKey] || {
        budget: null,
        actual: null,
      };
      if ((currentMonthData[field] ?? null) === numVal) {
        cancelCellEdit();
        return;
      }

      try {
        await dispatch(
          updateBudgetCategoryCell({
            property_id: record?.property_id ?? propertyId,
            fiscal_year: record?.fiscal_year ?? fiscalYear,
            category_id: record?.category_id ?? category!.category_id,
            category_type: record?.category_type ?? category!.category_type,
            month: monthKey,
            field,
            amount: numVal,
          }),
        ).unwrap();
      } catch (err: any) {
        const msg =
          err?.response?.data?.message || err?.message || "更新に失敗しました";
        showToast({ message: msg, type: "error" });
      }
      cancelCellEdit();
    },
    [
      dispatch,
      list.data,
      categories.data,
      resolveBudgetRowId,
      orderedKeys,
      editingValue,
      showToast,
      propertyId,
      fiscalYear,
    ],
  );

  // バッティングセルの警告アイコンクリックで確認モーダルを開く。手動値/積み上げの初期入力もセット。
  const openConflictModal = useCallback(
    (
      sortKey: string,
      colIdx: number,
      field: "budget" | "actual",
      conf: { manual: number | null; accumulated: number | null },
    ) => {
      setConflictModal({
        sortKey,
        colIdx,
        field,
        manual: conf.manual,
        accumulated: conf.accumulated,
        choice: "manual",
      });
      conflictForm.reset({
        manualAmount: conf.manual != null ? String(conf.manual) : "",
        accumAmount: conf.accumulated != null ? String(conf.accumulated) : "",
      });
    },
    [conflictForm],
  );

  // 内訳(項目)の競合モーダル: 手動実績(manual) vs 入金管理(payment)。
  const openDetailConflictModal = useCallback(
    (
      sortKey: string,
      colIdx: number,
      detailLabel: string,
      conf: { manual: number | null; payment: number },
    ) => {
      setConflictModal({
        sortKey,
        colIdx,
        field: "actual",
        manual: conf.manual,
        accumulated: conf.payment,
        choice: "manual",
        detailLabel,
      });
      conflictForm.reset({
        manualAmount: conf.manual != null ? String(conf.manual) : "",
        accumAmount: String(conf.payment),
      });
    },
    [conflictForm],
  );

  // #4: モーダルで選んだ値を反映。
  //  - 手動入力値を採用 → その (編集後) 値を手動値として確定 (manual_override 維持)。
  //  - 積み上げを採用: 値が積み上げのまま → 手動解除 (null=自動連動)。編集していれば手動値として確定。
  const handleApplyConflict = useCallback(async () => {
    const c = conflictModal;
    setConflictModal(null);
    if (!c) return;
    const record = list.data.find((r) => r.sort_key === c.sortKey);
    const monthKey = orderedKeys[c.colIdx];
    if (!record || !monthKey) return;
    const vals = conflictForm.getValues();
    const parse = (s: string): number | null => {
      const t = (s ?? "").toString().trim();
      if (t === "") return null;
      const v = Number(t.replace(/,/g, ""));
      return isNaN(v) ? null : v;
    };
    let amount: number | null;
    if (c.choice === "manual") {
      amount = parse(vals.manualAmount);
    } else {
      const v = parse(vals.accumAmount);
      // 積み上げをそのまま採用 = 手動解除 (自動連動に戻す)。編集していれば手動値として確定。
      amount = v === c.accumulated ? null : v;
    }
    try {
      if (c.detailLabel) {
        // 内訳(項目): manual_actual を set/clear。「入金を採用」(amount=null) で入金管理に戻す。
        // モーダルでの採用は「解決済み」なので手動採用時は actual_source="manual" を付与し、
        // 値が入金と食い違っていても以後！を出さない (採用したら消える)。
        await dispatch(
          updateBudgetDetailManualActual({
            property_id: record.property_id,
            fiscal_year: record.fiscal_year,
            category_id: record.category_id,
            category_type: record.category_type,
            detail_label: c.detailLabel,
            month: monthKey,
            value: amount,
            actual_source: c.choice === "manual" ? "manual" : "auto",
          }),
        ).unwrap();
      } else {
        await dispatch(
          updateBudgetCategoryCell({
            property_id: record.property_id,
            fiscal_year: record.fiscal_year,
            category_id: record.category_id,
            category_type: record.category_type,
            month: monthKey,
            field: c.field,
            amount,
            // 手動採用は「解決済み」→ 以後、積み上げと食い違っても！を出さない。
            resolved: c.choice === "manual",
          }),
        ).unwrap();
      }
      // 反映後に予実を再取得して即時反映する (リロード不要に)。
      await dispatch(
        fetchBudgets({ property_id: propertyId, fiscal_year: fiscalYear }),
      ).unwrap();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || "更新に失敗しました";
      showToast({ message: msg, type: "error" });
    }
  }, [
    conflictModal,
    list.data,
    orderedKeys,
    dispatch,
    showToast,
    conflictForm,
    propertyId,
    fiscalYear,
  ]);

  const commitDetailCellEdit = useCallback(
    async (
      sortKey: string,
      detailId: string,
      monthIdx: number,
      field: "budget" | "actual",
    ) => {
      const record = list.data.find((r) => r.sort_key === sortKey);
      if (!record || !record.details) {
        showToast({
          message: "対象の予算レコードが見つかりません",
          type: "error",
        });
        cancelCellEdit();
        return;
      }

      // 詳細セルは ExpenseRecord / PaymentRecord に書き込む経路のため数値必須。
      // クリア (null) は扱わない (§7-I はカテゴリセルが対象)。
      const parsed = parseEditValue();
      if (parsed === "invalid") {
        showToast({ message: "数値を入力してください", type: "error" });
        cancelCellEdit();
        return;
      }
      // 空欄のまま離脱 (未入力 / 既存値を消去) は「変更なし」として扱い、エラーは出さない。
      // 詳細セルはクリア非対応のため既存値を保持する (#1/#2: 数値未入力での離脱トースト解消)。
      if (parsed === null) {
        cancelCellEdit();
        return;
      }
      const numVal: number = parsed;

      const monthKey = orderedKeys[monthIdx];
      if (!monthKey) {
        showToast({ message: "対象月の特定に失敗しました", type: "error" });
        cancelCellEdit();
        return;
      }

      const targetDetail = record.details.find(
        (d) => (d.section_id || d.label) === detailId,
      );
      if (!targetDetail) {
        showToast({
          message: "対象の内訳が見つかりません",
          type: "error",
        });
        cancelCellEdit();
        return;
      }
      if (
        !canEditBudgetDetailCell({
          row: { group: record.category_type },
          detail: {
            id: targetDetail.section_id || targetDetail.label,
            label: targetDetail.label,
            sectionId: targetDetail.section_id,
            sectionCategory: targetDetail.section_category,
            revenueType: targetDetail.revenue_type,
            months: orderedKeys.map((key) => ({
              budget: targetDetail.months?.[key]?.budget ?? null,
              actual: targetDetail.months?.[key]?.actual ?? null,
              manual_actual:
                (targetDetail.months?.[key] as any)?.manual_actual ?? null,
              actual_source: (targetDetail.months?.[key] as any)?.actual_source,
            })),
          },
          monthIndex: monthIdx,
          monthKey,
          fiscalYear,
          field,
          viewMode,
        })
      ) {
        showToast({
          message: "個別支出行は発生日の月だけ編集できます",
          type: "error",
        });
        cancelCellEdit();
        return;
      }

      // 値が変わっていなければ何もしない (未編集で離脱した時に cell-payment / cell-expense を
      // 再 POST して二重計上されるのを防ぐ。カテゴリセル commitCellEdit と同じ未変更ガード)。
      const currentDetailVal = targetDetail.months?.[monthKey]?.[field] ?? null;
      if (currentDetailVal === numVal) {
        cancelCellEdit();
        return;
      }

      // 手動値と積み上げ(明細合計)は分離 (タスクA)。明細編集でカテゴリの手動値は
      // 自動反映しない (自動マージ廃止)。差異は警告アイコン + 確認モーダルで解決する。
      await executeDetailCellUpdate({
        sortKey,
        detailId,
        monthKey,
        field,
        amount: numVal,
      });
    },
    [
      list.data,
      orderedKeys,
      editingValue,
      showToast,
      categoryNameById,
      executeDetailCellUpdate,
      fiscalYear,
      viewMode,
    ],
  );

  /**
   * 支出追加 drawer 経由で ExpenseRecord を作成。
   * 支払済にすると postExpenseActual が actual にポストしつつ details にも反映。
   * これにより予実の actual は必ず ExpenseRecord 経由で更新される。
   */
  // Budget からの支出登録は ExpenseCreateDrawer → ExpenseDetailPage に移譲済み

  const handleInlineAdd = useCallback(
    async (targetGroup: "expense_opex" | "expense_capex" = "expense_opex") => {
      // 支出カテゴリはシステム標準のみ選択可。カスタム作成は廃止。
      const categoryId = newExpenseCategoryId;
      if (!categoryId) {
        showToast({ message: "科目を選択してください", type: "error" });
        return;
      }

      setAddingSubmitting(true);
      try {
        await dispatch(
          createBudget({
            property_id: propertyId,
            fiscal_year: fiscalYear,
            category_id: categoryId,
            // 行グループに応じて BudgetRecord.category_type を上書き
            // (BE 側で対応していなければ BudgetCategory のデフォルトが使われる)
            category_type: targetGroup,
            months: buildEmptyMonths(orderedKeys),
            source: "manual",
          } as any),
        ).unwrap();
        const catName =
          categories.data.find((c) => c.category_id === categoryId)?.name ||
          "科目";
        showToast({ message: `${catName} を追加しました`, type: "success" });
        dispatch(
          fetchBudgets({ property_id: propertyId, fiscal_year: fiscalYear }),
        );
        setAddingExpense(false);
        setNewExpenseCategoryId("");
      } catch {
        showToast({ message: "追加に失敗しました", type: "error" });
      } finally {
        setAddingSubmitting(false);
      }
    },
    [
      dispatch,
      propertyId,
      fiscalYear,
      orderedKeys,
      showToast,
      categories.data,
      newExpenseCategoryId,
    ],
  );

  /**
   * 科目追加 (新方式 §3.3)。既存科目名なら再利用、新規名なら BudgetCategory を作成し、
   * その科目の BudgetRecord を作って予実表に行を出す。
   * @param categoryType BudgetRecord の category_type (income / expense_opex / expense_capex)
   * @param revenueGroup 収入のみ必須 (stock_rent / stock_other / other)
   * @param name 入力された科目名
   */
  const handleAddCategory = useCallback(
    async (
      categoryType: "income" | "expense_opex" | "expense_capex",
      revenueGroup: "stock_rent" | "stock_other" | "other" | undefined,
      name: string,
      expenseGroup?: string,
    ) => {
      const trimmed = name.trim();
      if (!trimmed) {
        showToast({ message: "科目名を入力してください", type: "error" });
        return;
      }
      // 同期ガード: onAddNewOption + onValueChange の二重発火で同名科目が 2 個
      // 作られるのを防ぐ。ref は最初の await より前に立つので 2 回目は確実に弾かれる。
      if (addCategoryInFlightRef.current) return;
      addCategoryInFlightRef.current = true;
      setAddCategorySubmitting(true);
      try {
        // 1. 同 (category_type + revenue_group/expense_group + name) の既存科目を探す
        const isIncome = categoryType === "income";
        const existing = categories.data.find((c: any) => {
          if (c.deleted_at) return false;
          if (c.name !== trimmed) return false;
          if (isIncome) {
            return (
              c.category_type === "income" &&
              (c.revenue_group ?? undefined) === revenueGroup
            );
          }
          // 支出は OPEX/CAPEX/expense を許容。expense_group も一致条件に含める。
          return (
            (c.category_type === "expense" ||
              c.category_type === "expense_opex" ||
              c.category_type === "expense_capex") &&
            (c.expense_group ?? undefined) === (expenseGroup ?? undefined)
          );
        });

        // 2. 完全同名 (同 type+group+name) が既に予実表へ追加済みならエラー。
        //    同じ科目が 2 行に増えるのを防ぐ。
        let categoryId = existing?.category_id as string | undefined;
        if (categoryId && list.data.some((r) => r.category_id === categoryId)) {
          showToast({
            message: `科目「${trimmed}」は既に追加されています`,
            type: "error",
          });
          return;
        }
        // 支出は親カテゴリ(level="category")配下の明細科目(子)として作る。
        // expense_group が一致する親カテゴリを解決し parent_category_id を渡す
        // (親が見つからない旧データは従来どおり expense_group 直指定にフォールバック)。
        let parentCategoryId: string | undefined;
        if (!isIncome && expenseGroup) {
          const parentCat = categories.data.find(
            (c: any) =>
              !c.deleted_at &&
              c.level === "category" &&
              (c.expense_group ?? undefined) === expenseGroup,
          );
          parentCategoryId = parentCat?.category_id;
        }
        // 3. 無ければ新規作成 (収入は revenue_group 必須 / 支出は親配下の子)。
        //    BE 側も一意制約で 409 を返す (多重防御)。
        if (!categoryId) {
          const created: any = await dispatch(
            createBudgetCategory({
              name: trimmed,
              category_type: categoryType,
              ...(isIncome
                ? { revenue_group: revenueGroup }
                : parentCategoryId
                  ? { parent_category_id: parentCategoryId }
                  : expenseGroup
                    ? { expense_group: expenseGroup }
                    : {}),
            } as any),
          ).unwrap();
          categoryId = created.category_id;
        }
        if (!categoryId) throw new Error("category_id 解決に失敗");

        // 3. その科目の BudgetRecord を作成 (予実表に行が出る)
        await dispatch(
          createBudget({
            property_id: propertyId,
            fiscal_year: fiscalYear,
            category_id: categoryId,
            category_type: categoryType,
            months: buildEmptyMonths(orderedKeys),
            source: "manual",
          } as any),
        ).unwrap();

        showToast({ message: `${trimmed} を追加しました`, type: "success" });
        await dispatch(
          fetchBudgets({ property_id: propertyId, fiscal_year: fiscalYear }),
        );
        setAddCategoryFor(null);
      } catch (e: any) {
        showToast({
          message: e?.message || e?.detail || "科目の追加に失敗しました",
          type: "error",
        });
      } finally {
        addCategoryInFlightRef.current = false;
        setAddCategorySubmitting(false);
      }
    },
    [
      dispatch,
      propertyId,
      fiscalYear,
      orderedKeys,
      showToast,
      categories.data,
      list.data,
    ],
  );

  // 支出カテゴリ(親, level="category")だけを新規追加する (明細科目は作らない)。
  // 予実表では活性カテゴリとして空カテゴリ見出しが自動で出る。
  const handleAddExpenseCategory = useCallback(
    async (name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      if (addCategoryInFlightRef.current) return;
      addCategoryInFlightRef.current = true;
      setAddCategorySubmitting(true);
      try {
        await dispatch(
          createBudgetCategory({
            name: trimmed,
            category_type: "expense_opex",
            level: "category",
            expense_group: trimmed,
          } as any),
        ).unwrap();
        showToast({
          message: `カテゴリ「${trimmed}」を追加しました`,
          type: "success",
        });
        await dispatch(fetchBudgetCategories());
      } catch (e: any) {
        showToast({
          message: e?.message || e?.detail || "カテゴリの追加に失敗しました",
          type: "error",
        });
      } finally {
        addCategoryInFlightRef.current = false;
        setAddCategorySubmitting(false);
      }
    },
    [dispatch, showToast],
  );

  const toggleExpand = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // null/0 区別 (§7-I): null (未入力) は "-"、0 (明示ゼロ) は "¥0"。全セル共通。
  const fmt = (n: number | null) =>
    n === null || n === undefined ? "-" : formatYen(n);
  // fmtZero も同挙動 (旧: 0→¥0 固定。null 区別のため統一)。
  const fmtZero = (n: number | null) =>
    n === null || n === undefined ? "-" : formatYen(n);
  const fmtDiff = (n: number | null) => {
    if (n === null || n === undefined) return "-";
    if (n === 0) return "¥0";
    return formatSignedYen(n);
  };
  // 集計行用 (差異)。null は "-"、0 は ¥0。
  const fmtDiffZero = (n: number | null) => {
    if (n === null || n === undefined) return "-";
    if (n === 0) return "¥0";
    return formatSignedYen(n);
  };

  /**
   * 月セル値の合計 (§7-I)。全要素が null (未入力) なら null を返す。
   * 1 つでも数値があれば合計 (null は 0 扱い)。これで「全部未入力」と「合計 0」を区別する。
   */
  const sumCells = (vals: (number | null | undefined)[]): number | null => {
    let has = false;
    let sum = 0;
    for (const v of vals) {
      if (v !== null && v !== undefined) {
        has = true;
        sum += v;
      }
    }
    return has ? sum : null;
  };

  /** 差異は常に 実績−予算。収入は増が良(+が緑)、支出・CAPEX・融資は減が良(−が緑)。 */
  const diffVarianceColor = (
    groupKey: "income" | "expense" | "capex" | "loan",
    diff: number | null,
  ): string => {
    if (diff === null || diff === 0) return "#999";
    if (groupKey === "income") {
      return diff > 0 ? "#2E7D32" : "#e53935";
    }
    return diff < 0 ? "#2E7D32" : "#e53935";
  };

  /**
   * 差異 = 実績 − 予算。ただし「予算と実績の両方に数値が入っているとき」だけ計算する。
   * 片方でも未入力 (null) なら差異は出さない (null → 表示は "-")。
   */
  const calcDiff = (
    actual: number | null,
    budget: number | null,
  ): number | null =>
    actual !== null && budget !== null ? actual - budget : null;

  const categoryOrderMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of categories.data) {
      map.set(c.category_id, c.display_order ?? 9999);
    }
    return map;
  }, [categories.data]);

  const sortByDisplayOrder = (rows: BudgetRow[]) =>
    [...rows].sort((a, b) => {
      const aKey = a.id.split("#")[2] || "";
      const bKey = b.id.split("#")[2] || "";
      return (
        (categoryOrderMap.get(aKey) ?? 9999) -
        (categoryOrderMap.get(bKey) ?? 9999)
      );
    });

  const incomeRows = sortByDisplayOrder(
    data.filter((r) => r.group === "income"),
  );
  // 収入の 2 階層目 (貸室/その他収入/その他) 振り分け (§3.1)。
  // 科目の revenue_group で分類。判別できない (legacy) 行は「その他」に寄せる。
  const incomeSubGroups = useMemo(() => {
    const labelOf = (rg: string) =>
      rg === "stock_rent"
        ? "貸室"
        : rg === "stock_other"
          ? "付帯"
          : rg === "other"
            ? "その他"
            : rg; // カスタム収益カテゴリは値(=名称)をそのまま見出しに
    const byGroup = new Map<string, BudgetRow[]>();
    // system 3つは常に先頭に出す (空でも見出しを保つ)
    for (const rg of ["stock_rent", "stock_other", "other"])
      byGroup.set(rg, []);
    for (const r of incomeRows) {
      const rg =
        (r.categoryId ? revenueGroupById.get(r.categoryId) : undefined) ||
        "other";
      if (!byGroup.has(rg)) byGroup.set(rg, []); // カスタム revenue_group
      byGroup.get(rg)!.push(r);
    }
    return Array.from(byGroup, ([rg, rows]) => ({
      key: `income_${rg}`,
      label: labelOf(rg),
      rows,
    }));
  }, [incomeRows, revenueGroupById]);
  // OPEX（旧 expense + 新 expense_opex）
  const opexRows = sortByDisplayOrder(
    data.filter(
      (r) =>
        r.group === "expense_opex" ||
        (r.group === "expense" && r.category !== "工事費（CAPEX）"),
    ),
  );
  // CAPEX
  const capexRows = sortByDisplayOrder(
    data.filter((r) => r.group === "expense_capex"),
  );
  // 融資返済
  const loanRows = sortByDisplayOrder(data.filter((r) => r.group === "loan"));
  // 後方互換: expenseRows は OPEX のエイリアス
  const expenseRows = opexRows;

  // 支出 (OPEX) の 2 階層目 (カテゴリ) 振り分け。ユーザー管理の expense_group で分類する。
  const expenseSubGroups = (() => {
    const byGroup = new Map<string, BudgetRow[]>();
    for (const r of opexRows) {
      const g =
        (r.categoryId ? expenseGroupById.get(r.categoryId) : undefined) ||
        "その他";
      if (!byGroup.has(g)) byGroup.set(g, []);
      byGroup.get(g)!.push(r);
    }
    return Array.from(byGroup, ([g, rows]) => ({
      key: `expense_${g}`,
      label: g,
      rows,
    }));
  })();

  // 合計の集計は getTotalVal に集約 (各行の表示値=getVal を sumCells)。
  // 全行 null なら null (→ "-")、1 つでも数値があればその和 (null は 0 扱い)。

  const displayColumns =
    viewMode === "monthly" ? MONTHS : ["Q1", "Q2", "Q3", "Q4"];

  const ytd = useMemo(() => {
    const slice = (rows: BudgetRow[], field: "budget" | "actual") =>
      rows.reduce(
        (acc, r) => acc + r.months.reduce((a, m) => a + (m[field] ?? 0), 0),
        0,
      );

    // 収入を専有部(賃料)/付帯設備に分割
    // 新スキーマ: revenue_type === "ancillary_income" を付帯収益とみなす
    const isAncillaryRow = (r: BudgetRow) =>
      r.details &&
      r.details.length > 0 &&
      r.details[0].revenueType === "ancillary_income";
    const privateRows = incomeRows.filter((r) => !isAncillaryRow(r));
    const ancillaryRows = incomeRows.filter((r) => isAncillaryRow(r));

    const pb = slice(privateRows, "budget");
    const pa = slice(privateRows, "actual");
    const ab = slice(ancillaryRows, "budget");
    const aa = slice(ancillaryRows, "actual");
    const ib = pb + ab;
    const ia = pa + aa;
    const eb = slice(expenseRows, "budget");
    const ea = slice(expenseRows, "actual");
    const cb = slice(capexRows, "budget");
    const ca = slice(capexRows, "actual");
    const lb = slice(loanRows, "budget");
    const la = slice(loanRows, "actual");
    const noiBudget = ib - eb;
    const noiActual = ia - ea;
    return {
      ib,
      ia,
      eb,
      ea,
      pb,
      pa,
      ab,
      aa,
      noiBudget,
      noiActual,
      ncfBudget: noiBudget - cb - lb,
      ncfActual: noiActual - ca - la,
      // 支出合計 = OPEX + CAPEX (融資返済は経常費用ではないため除外)
      expTotalBudget: eb + cb,
      expTotalActual: ea + ca,
    };
  }, [data, incomeRows, expenseRows, capexRows, loanRows]);

  const SummaryCard = CustomSummaryCard;

  const cellSx = {
    fontSize: "14px",
    py: 1,
    px: 1,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    // border-bottom はズーム/サブピクセル位置でちらつくため inset boxShadow で描画
    borderBottom: "none",
    boxShadow: "inset 0 -1px 0 #E0E0E0",
  };

  // 内訳の実績表示値: 手動実績(予実=manual_actual) があればそれ、無ければ actual(入金管理)。
  // 予算(budget)はそのまま。→ 表示・親の積み上げ・競合判定をこの優先順位で揃える。
  const detailFieldVal = (
    cell: BudgetDetailRow["months"][number] | undefined,
    field: "budget" | "actual",
  ): number | null => {
    if (!cell) return null;
    if (field === "actual") return cell.manual_actual ?? cell.actual ?? null;
    return cell[field] ?? null;
  };

  const getVal = (
    row: BudgetRow,
    colIdx: number,
    field: "budget" | "actual",
  ) => {
    // 表示優先順位:
    //   1. その月セルが manual_override.{field}=true なら手動値を表示
    //   2. それ以外は内訳 (details) の合計 (実績は手動実績優先)
    //   3. 内訳が無ければ months[].field をそのまま (BE 集計済み)
    const sumAt = (idx: number): number | null => {
      const cell = row.months[idx];
      const isManual = !!cell?.manual_override?.[field];
      if (isManual) return cell?.[field] ?? null;
      if (row.details && row.details.length > 0) {
        return sumCells(
          row.details.map((d) => detailFieldVal(d.months?.[idx], field)),
        );
      }
      return cell?.[field] ?? null;
    };
    if (viewMode === "monthly") return sumAt(colIdx);
    const start = colIdx * 3;
    return sumCells([sumAt(start), sumAt(start + 1), sumAt(start + 2)]);
  };

  // #4: 手動値 (manual_override セル) と積み上げ (明細合計) のバッティング検出。
  // 月次ビューのみ。両者が異なれば {手動値, 積み上げ} を返し、無ければ null。
  const getCellConflict = (
    row: BudgetRow,
    colIdx: number,
    field: "budget" | "actual",
  ): { manual: number | null; accumulated: number | null } | null => {
    if (viewMode !== "monthly") return null;
    const cell = row.months[colIdx];
    if (!cell?.manual_override?.[field]) return null;
    if (!row.details || row.details.length === 0) return null;
    // モーダルで手動採用済み (解決済み) は食い違っても警告を出さない (採用したら消える)。
    if (cell?.override_resolved?.[field]) return null;
    const manual = cell[field] ?? null;
    const accumulated = sumCells(
      row.details.map((d) => detailFieldVal(d.months?.[colIdx], field)),
    );
    // 積み上げが未入力 (null) のときはバッティングとしない。
    if (accumulated === null) return null;
    if ((manual ?? null) === accumulated) return null;
    return { manual, accumulated };
  };

  const getTotalVal = (
    rows: BudgetRow[],
    colIdx: number,
    field: "budget" | "actual",
  ): number | null =>
    // 合計は各行の「表示値」(手動値 → 明細積み上げ → 生値) を集計する。getVal が
    // viewMode (monthly/quarter) を内部分岐するので colIdx をそのまま渡せばよい。
    // 生の months[field] を直接合計すると、積み上げ採用で手動値を null クリアした
    // 行が合計から欠落する (D3: 貸室/収入の合計が消える) ため getVal 経由にする。
    sumCells(rows.map((r) => getVal(r, colIdx, field)));

  const getDetailVal = (
    detail: BudgetDetailRow,
    colIdx: number,
    field: "budget" | "actual",
  ) => {
    // 実績は「手動実績(予実) 優先、無ければ入金管理」で表示。予算はそのまま。
    if (viewMode === "monthly")
      return detailFieldVal(detail.months[colIdx], field);
    const start = colIdx * 3;
    return sumCells([
      detailFieldVal(detail.months[start], field),
      detailFieldVal(detail.months[start + 1], field),
      detailFieldVal(detail.months[start + 2], field),
    ]);
  };

  // 内訳 (項目) の競合: 手動実績(manual_actual) と 入金管理(actual) が両方あり、
  // 異なれば返す。月次ビューのみ。優先はない (どちらかを自動採用しない)。
  //  - actual_source === "manual" はモーダルで手動を採用済み (解決済み) なので
  //    値が食い違っていても！を出さない (採用したら消える)。
  const getDetailConflict = (
    detail: BudgetDetailRow,
    colIdx: number,
  ): { manual: number; payment: number } | null => {
    if (viewMode !== "monthly") return null;
    const cell = detail.months[colIdx];
    const manual = cell?.manual_actual ?? null;
    const payment = cell?.actual ?? null;
    if (manual === null || payment === null) return null;
    if (manual === payment) return null;
    if (cell?.actual_source === "manual") return null;
    return { manual, payment };
  };

  // ===================== 仮想化テーブル (新描画・β) =====================
  // 既存ロジックを流用し RowVM[] を構築。editingValue は依存に含めない
  // (入力中に全行を再構築しないため → 軽い)。フラグ ON で旧表示と差し替え。
  const [vStickyWidth, setVStickyWidth] = useState(220);
  const [vAddCategory, setVAddCategory] = useState<{
    categoryType: "income" | "expense_opex" | "expense_capex";
    revenueGroup?: string;
    expenseGroup?: string;
    /** "category"=カテゴリ(親)を追加するモード。未指定は明細科目(子)追加。 */
    level?: "category" | "item";
  } | null>(null);
  // 科目追加モーダルの明細項目名 (収入の「科目を新規登録」モーダルと同じ 戻る/登録 方式)
  const [vAddName, setVAddName] = useState("");
  // 同名エラーはトーストではなく input 下にインライン表示する (C3)
  const [vAddError, setVAddError] = useState<string | null>(null);
  const vEditTargetRef = useRef<{
    sortKey: string;
    ci: number;
    field: "budget" | "actual";
    detailId?: string;
  } | null>(null);
  const vEditInitialRef = useRef(""); // 編集開始時の表示文字列 (未変更blur判定)

  const rowById = useMemo(() => {
    const m = new Map<string, BudgetRow>();
    for (const r of data) m.set(r.id, r);
    return m;
  }, [data]);

  const rowsVM = useMemo<RowVM[]>(() => {
    const out: RowVM[] = [];
    const editable = viewMode === "monthly";
    const cols = displayColumns;
    const catLabel = (row: BudgetRow) =>
      categoryNameById.get(row.categoryId || "") || row.category;

    const totalCells = (
      rows: BudgetRow[],
      colorKey: "income" | "expense",
    ): RowVMCell[] =>
      cols.map((_, ci) => {
        const b = getTotalVal(rows, ci, "budget");
        const a = getTotalVal(rows, ci, "actual");
        const d = calcDiff(a, b);
        return {
          budgetText: fmtZero(b),
          actualText: fmtZero(a),
          diffText: fmtDiffZero(d),
          diffColor: diffVarianceColor(colorKey, d),
          editableBudget: false,
          editableActual: false,
          conflictBudget: false,
          conflictActual: false,
        };
      });

    const pushCategory = (
      row: BudgetRow,
      depth: number,
      colorKey: "income" | "expense",
    ) => {
      const catOpen = !!expanded[row.id];
      out.push({
        key: `cat:${row.id}`,
        kind: "category",
        pl: 8 + depth * 16,
        bold: false,
        label: catLabel(row),
        groupBg: false,
        expandable: true,
        expanded: catOpen,
        toggleKey: row.id,
        sortKey: row.id,
        cells: cols.map((_, ci) => {
          const b = getVal(row, ci, "budget");
          const a = getVal(row, ci, "actual");
          const d = calcDiff(a, b);
          // 積み上げ(明細合計)がある科目セルは手動変更をモーダル経由に誘導する (月次のみ)。
          const hasDetails = !!row.details && row.details.length > 0;
          const accB = hasDetails
            ? sumCells(
                row.details!.map((dd) =>
                  detailFieldVal(dd.months?.[ci], "budget"),
                ),
              )
            : null;
          const accA = hasDetails
            ? sumCells(
                row.details!.map((dd) =>
                  detailFieldVal(dd.months?.[ci], "actual"),
                ),
              )
            : null;
          const gatable = viewMode === "monthly" && editable;
          return {
            budgetText: fmt(b),
            actualText: fmt(a),
            diffText: fmtDiff(d),
            diffColor: diffVarianceColor(colorKey, d),
            editableBudget: editable,
            editableActual: editable,
            conflictBudget: !!getCellConflict(row, ci, "budget"),
            conflictActual: !!getCellConflict(row, ci, "actual"),
            gateBudget: gatable && accB !== null,
            gateActual: gatable && accA !== null,
          };
        }),
      });
      if (!catOpen) return;
      (row.details ?? []).forEach((detail) => {
        const isIncome = colorKey === "income";
        let link: LinkAction | undefined;
        if (
          isIncome &&
          detail.sectionId &&
          (detail.revenueType === "rent" ||
            detail.revenueType === "ancillary_income")
        )
          link = { type: "section", id: detail.sectionId };
        else if (!isIncome && detail.id)
          link = { type: "expense", id: detail.id };
        out.push({
          key: `det:${row.id}:${detail.id}`,
          kind: "detail",
          // 内訳は科目の少し右 (深くしすぎない)
          pl: 8 + depth * 16 + 2,
          bold: false,
          label: detail.label,
          groupBg: false,
          link: !!link,
          linkAction: link,
          expandable: false,
          expanded: false,
          sortKey: row.id,
          detailId: detail.id,
          detailLabel: detail.label,
          cells: cols.map((_, ci) => {
            const b = getDetailVal(detail, ci, "budget");
            const a = getDetailVal(detail, ci, "actual");
            const d = calcDiff(a, b);
            const monthKey = orderedKeys[ci] || "";
            const canEditDetailBudget = canEditBudgetDetailCell({
              row,
              detail,
              monthIndex: ci,
              monthKey,
              fiscalYear,
              field: "budget",
              viewMode,
            });
            const canEditDetailActual = canEditBudgetDetailCell({
              row,
              detail,
              monthIndex: ci,
              monthKey,
              fiscalYear,
              field: "actual",
              viewMode,
            });
            // 入金(積み上げ)がある月は手動変更をモーダル経由に誘導する (月次ビューのみ)。
            const paymentPresent =
              viewMode === "monthly" &&
              (detail.months[ci]?.actual ?? null) !== null;
            return {
              budgetText: fmt(b),
              actualText: fmt(a),
              diffText: fmtDiff(d),
              diffColor: diffVarianceColor(colorKey, d),
              editableBudget: canEditDetailBudget,
              editableActual: canEditDetailActual,
              conflictBudget: false,
              conflictActual:
                canEditDetailActual && !!getDetailConflict(detail, ci),
              gateActual: canEditDetailActual && paymentPresent,
            };
          }),
        });
      });
      let addAction: AddAction;
      let addLabel: string;
      if (colorKey === "income") {
        const rg = revenueGroupById.get(row.categoryId || "") || "other";
        addAction =
          rg === "other"
            ? { kind: "incomePayment" }
            : {
                kind: "incomeUnit",
                modalKind: rg === "stock_other" ? "ancillary" : "private_area",
              };
        addLabel = "名称追加";
      } else {
        addAction = {
          kind: "expense",
          rowCategory: catLabel(row),
          rowCategoryId: row.categoryId,
          rowGroup: row.group,
        };
        addLabel = "支出追加";
      }
      out.push({
        key: `add:${row.id}`,
        kind: "addDetail",
        // 収入・支出とも内訳追加行は科目配下として同じ深さ (56) にネストする。
        pl: 56,
        bold: false,
        label: addLabel,
        groupBg: true,
        expandable: false,
        expanded: false,
        addAction,
        cells: [],
      });
    };

    // 収入
    const incomeOpen = !!groupExpanded.income;
    out.push({
      key: "grp:income",
      kind: "group",
      pl: 8,
      bold: true,
      label: "収入",
      groupBg: true,
      expandable: true,
      expanded: incomeOpen,
      toggleKey: "income",
      cells: totalCells(incomeRows, "income"),
    });
    if (incomeOpen) {
      for (const sg of incomeSubGroups) {
        const sgOpen = !!groupExpanded[sg.key];
        out.push({
          key: `grp:${sg.key}`,
          kind: "subgroup",
          pl: 24,
          bold: false,
          label: sg.label,
          groupBg: true,
          expandable: true,
          expanded: sgOpen,
          toggleKey: sg.key,
          cells: totalCells(sg.rows, "income"),
        });
        if (sgOpen) {
          // その他も貸室/付帯と同様に科目(明細項目)行を描画する。unit 紐付け入金の
          // その他 item (例: 101 の「イベント費」) は区画明細付き、unit 非紐付け入金は
          // カテゴリ集計 (区画明細なし) で、この科目行の actual に積み上がる。
          // 旧「その他=1入金1行」描画は廃止 (unit 紐付けその他 item が表示されず
          // 合計だけ増える不整合への対処)。
          for (const row of sg.rows) pushCategory(row, 2, "income");
          const rg = sg.key.replace("income_", "");
          // 科目(明細項目)の追加導線 (全サブグループ共通)。
          out.push({
            key: `addcat:${sg.key}`,
            kind: "addCategory",
            pl: 40,
            bold: false,
            label: "明細項目追加",
            groupBg: true,
            expandable: false,
            expanded: false,
            addAction: {
              kind: "addCategory",
              categoryType: "income",
              revenueGroup: rg,
              addKey: sg.key,
            },
            cells: [],
          });
          // 実績入金の登録は収入タブの責務のため、予実表(その他)の「収入追加」は廃止。
          // 予実表は明細項目(科目)の追加・予算入力に専念する。
        }
      }
    }

    // 支出 (OPEX) — 収入と同様にカテゴリ(expense_group)のサブグループで階層化。
    const opexOpen = !!groupExpanded.expense;
    out.push({
      key: "grp:expense",
      kind: "group",
      pl: 8,
      bold: true,
      label: "支出(OPEX)",
      groupBg: true,
      expandable: true,
      expanded: opexOpen,
      toggleKey: "expense",
      cells: totalCells(opexRows, "expense"),
    });
    if (opexOpen) {
      for (const sg of expenseSubGroups) {
        const sgOpen = !!groupExpanded[sg.key];
        out.push({
          key: `grp:${sg.key}`,
          kind: "subgroup",
          pl: 24,
          bold: false,
          label: sg.label,
          groupBg: true,
          expandable: true,
          expanded: sgOpen,
          toggleKey: sg.key,
          cells: totalCells(sg.rows, "expense"),
        });
        if (sgOpen) {
          for (const row of sg.rows) {
            // level="category" (カテゴリ親) は見出し(サブグループ)のみ。明細行
            // としては描画しない (空カテゴリ表示 & カテゴリ名の 2重表示回避)。
            if (categoryLevelById.get(row.categoryId || "") === "category") {
              continue;
            }
            pushCategory(row, 2, "expense");
          }
          const eg = sg.label; // expense_group の値 = 表示名
          out.push({
            key: `addcat:${sg.key}`,
            kind: "addCategory",
            pl: 40,
            bold: false,
            label: "明細項目追加",
            groupBg: true,
            expandable: false,
            expanded: false,
            addAction: {
              kind: "addCategory",
              categoryType: "expense_opex",
              expenseGroup: eg,
              addKey: sg.key,
            },
            cells: [],
          });
        }
      }
      // 支出カテゴリ(親)を新規追加する導線 (モーダルでカテゴリだけ追加)。
      out.push({
        key: "addExpenseCategory",
        kind: "addCategory",
        pl: 24,
        bold: false,
        label: "カテゴリ追加",
        groupBg: true,
        expandable: false,
        expanded: false,
        addAction: { kind: "addExpenseCategory" },
        cells: [],
      });
    }

    // NOI = 収入 − OPEX
    out.push({
      key: "noi",
      kind: "derived",
      pl: 16,
      bold: true,
      label: "NOI",
      groupBg: true,
      derivedBg: "#E8F4FD",
      expandable: false,
      expanded: false,
      cells: cols.map((_, ci) => {
        const b = subtractWhenBothEntered(
          getTotalVal(incomeRows, ci, "budget"),
          getTotalVal(opexRows, ci, "budget"),
        );
        const a = subtractWhenBothEntered(
          getTotalVal(incomeRows, ci, "actual"),
          getTotalVal(opexRows, ci, "actual"),
        );
        const d = calcDiff(a, b);
        return {
          budgetText: fmtZero(b),
          actualText: fmtZero(a),
          diffText: fmtDiffZero(d),
          diffColor: diffVarianceColor("income", d),
          budgetColor: b !== null && b < 0 ? colors.status.error : undefined,
          actualColor: a !== null && a < 0 ? colors.status.error : undefined,
          editableBudget: false,
          editableActual: false,
          conflictBudget: false,
          conflictActual: false,
        };
      }),
    });

    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    incomeRows,
    opexRows,
    incomeSubGroups,
    expenseSubGroups,
    orderedKeys,
    displayColumns,
    expanded,
    groupExpanded,
    viewMode,
    fiscalYear,
    categoryNameById,
    revenueGroupById,
    expenseGroupById,
  ]);

  const vToggle = useCallback((vm: RowVM) => {
    const k = vm.toggleKey;
    if (!k) return;
    if (vm.kind === "group" || vm.kind === "subgroup")
      setGroupExpanded((p) => ({ ...p, [k]: !p[k] }));
    else if (vm.kind === "category") setExpanded((p) => ({ ...p, [k]: !p[k] }));
  }, []);

  const vStartEdit = useCallback(
    (vm: RowVM, ci: number, field: "budget" | "actual") => {
      const row = rowById.get(vm.sortKey || "");
      if (!row) return;
      let cur: number | null;
      if (vm.detailId) {
        const detail = (row.details ?? []).find((d) => d.id === vm.detailId);
        cur = detail ? getDetailVal(detail, ci, field) : null;
      } else {
        cur = getVal(row, ci, field);
      }
      vEditTargetRef.current = {
        sortKey: row.id,
        ci,
        field,
        detailId: vm.detailId,
      };
      // startCellEdit と同じ初期表示文字列を記録 (未変更blur判定用)
      vEditInitialRef.current =
        cur === null || cur === 0 ? "" : cur.toLocaleString("ja-JP");
      startCellEdit(row.id, ci, field, cur, vm.detailId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowById],
  );

  const vCommitEdit = useCallback(() => {
    const t = vEditTargetRef.current;
    if (!t) return;
    // 何も変更していない (初期値のまま) なら更新せずキャンセル。
    // → 入力モードにして空欄のまま戻しても null 更新されない。
    if (editingValue === vEditInitialRef.current) {
      vEditTargetRef.current = null;
      cancelCellEdit();
      return;
    }
    if (t.detailId) commitDetailCellEdit(t.sortKey, t.detailId, t.ci, t.field);
    else commitCellEdit(t.sortKey, t.ci, t.field);
    vEditTargetRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingValue]);

  const vCancelEdit = useCallback(() => {
    vEditTargetRef.current = null;
    cancelCellEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const vChangeEditValue = useCallback((val: string) => {
    setEditingValue(formatWithCommas(val));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const vOpenConflict = useCallback(
    (vm: RowVM, ci: number, field: "budget" | "actual") => {
      const row = rowById.get(vm.sortKey || "");
      if (!row) return;
      if (vm.detailId) {
        const detail = (row.details ?? []).find((d) => d.id === vm.detailId);
        if (!detail) return;
        const c = getDetailConflict(detail, ci);
        if (c) {
          openDetailConflictModal(
            row.id,
            ci,
            vm.detailLabel || detail.label,
            c,
          );
        } else {
          // 競合は無いが入金(積み上げ)がある場合、手動変更をモーダル経由で行う。
          const payment = detail.months[ci]?.actual ?? null;
          if (payment === null) return;
          const manual = detail.months[ci]?.manual_actual ?? null;
          openDetailConflictModal(row.id, ci, vm.detailLabel || detail.label, {
            manual,
            payment,
          });
        }
      } else {
        const c = getCellConflict(row, ci, field);
        if (c) {
          openConflictModal(row.id, ci, field, c);
        } else {
          // 競合は無いが積み上げ(明細合計)がある場合、手動変更をモーダル経由で行う。
          if (!row.details || row.details.length === 0) return;
          const accumulated = sumCells(
            row.details.map((d) => detailFieldVal(d.months?.[ci], field)),
          );
          if (accumulated === null) return;
          const manual = row.months[ci]?.[field] ?? null;
          openConflictModal(row.id, ci, field, { manual, accumulated });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowById],
  );

  const vLabelClick = useCallback((vm: RowVM) => {
    if (vm.linkAction) {
      const { type, id } = vm.linkAction;
      if (type === "section") setPreviewSectionId(id);
      else if (type === "expense") setPreviewExpenseId(id);
      else if (type === "payment") setIncomeDetailPayment(vm.paymentRaw as any);
      return;
    }
    if (vm.addAction) {
      const a = vm.addAction;
      if (a.kind === "incomePayment") setIncomePaymentOpen(true);
      else if (a.kind === "incomeUnit")
        setIncomeAddModal({ kind: a.modalKind });
      else if (a.kind === "expense")
        setExpenseDrawer({
          open: true,
          rowCategory: a.rowCategory,
          rowCategoryId: a.rowCategoryId,
          rowGroup: a.rowGroup as BudgetGroup,
        });
      else if (a.kind === "addCategory") {
        setVAddName("");
        setVAddError(null);
        setVAddCategory({
          categoryType: a.categoryType,
          revenueGroup: a.revenueGroup,
          expenseGroup: a.expenseGroup,
        });
      } else if (a.kind === "addExpenseCategory") {
        setVAddName("");
        setVAddError(null);
        setVAddCategory({ categoryType: "expense_opex", level: "category" });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const existingCategoryIds = useMemo(
    () => new Set(list.data.map((r) => r.category_id)),
    [list.data],
  );

  // テキストがはみ出した時だけ Tooltip を表示する wrapper
  const TruncatedWithTooltip: React.FC<{
    text: string;
    sx?: any;
    onClick?: (e: React.MouseEvent) => void;
  }> = ({ text, sx, onClick }) => {
    const [isTruncated, setIsTruncated] = useState(false);
    const inner = (
      <Box sx={{ minWidth: 0, ...(sx || {}) }} onClick={onClick}>
        <CustomTruncateText text={text} onTruncated={setIsTruncated} />
      </Box>
    );
    if (!isTruncated) return inner;
    return (
      <Tooltip title={text} placement="top" arrow>
        {inner}
      </Tooltip>
    );
  };

  /**
   * 「+科目を追加」行 (新方式 §3.3)。検索 + 新規追加できる CustomCategorySelectSingle。
   * @param addKey       この追加行を一意に識別するキー (addCategoryFor と照合)
   * @param categoryType BudgetRecord の category_type
   * @param revenueGroup 収入のみ (stock_rent / stock_other / other)
   * @param indentPl     1 列目の左パディング (階層に合わせる)
   */
  const renderAddCategoryRow = (
    addKey: string,
    categoryType: "income" | "expense_opex" | "expense_capex",
    revenueGroup: "stock_rent" | "stock_other" | "other" | undefined,
    indentPl: number | string,
  ): React.ReactNode => {
    const isOpen = addCategoryFor === addKey;
    // この枠で選べる既存科目候補 (同 category_type + revenue_group、未追加のもの)
    const isIncome = categoryType === "income";
    const options = (categories.data as any[])
      .filter((c) => {
        if (c.deleted_at) return false;
        if (existingCategoryIds.has(c.category_id)) return false;
        if (isIncome) {
          return (
            c.category_type === "income" &&
            (c.revenue_group ?? undefined) === revenueGroup
          );
        }
        return (
          c.category_type === "expense" ||
          c.category_type === "expense_opex" ||
          c.category_type === "expense_capex"
        );
      })
      .map((c) => ({ value: c.name, label: c.name }));

    if (!isOpen) {
      return (
        <TableRow sx={{ backgroundColor: "#FAFAFA", height: 44 }}>
          <TableCell
            sx={{
              ...cellSx,
              py: 0.5,
              pl: indentPl,
              position: "sticky",
              left: 0,
              zIndex: 1,
              backgroundColor: "#FAFAFA",
              width: vStickyWidth,
              minWidth: vStickyWidth,
              maxWidth: vStickyWidth,
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <Can action="budget:edit">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  cursor: "pointer",
                  color: "#999",
                  "&:hover": { color: "#0B9DBD" },
                }}
                onClick={() => setAddCategoryFor(addKey)}
              >
                <AddIcon sx={{ fontSize: 16 }} />
                <Typography sx={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                  明細項目追加
                </Typography>
              </Box>
            </Can>
          </TableCell>
          {displayColumns.map((m) => (
            <Fragment key={m}>
              <TableCell sx={{ ...cellSx, backgroundColor: "#FAFAFA" }} />
              <TableCell sx={{ ...cellSx, backgroundColor: "#FAFAFA" }} />
              <TableCell sx={{ ...cellSx, backgroundColor: "#FAFAFA" }} />
            </Fragment>
          ))}
        </TableRow>
      );
    }

    return (
      <TableRow sx={{ backgroundColor: "#FAFAFA", height: 44 }}>
        <TableCell
          sx={{
            ...cellSx,
            position: "sticky",
            left: 0,
            zIndex: 5,
            backgroundColor: "#FAFAFA",
            py: 0.5,
            // 科目列の幅は他行と必ず揃える。この行だけ広いと列全体が
            // 膨らみ、横スクロール時に sticky 列の右シャドウが他行とずれるため。
            width: vStickyWidth,
            minWidth: vStickyWidth,
            maxWidth: vStickyWidth,
            boxSizing: "border-box",
            overflow: "visible",
          }}
        >
          {/* 幅広セレクトは絶対配置で列幅に影響させない (列を広げないため)。
              全高 flex で縦中央に寄せ、indent は pl で付与。
              label="" でも描かれる空ラベル枠 (.inputLabel) は display:none で除去。*/}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              pl: indentPl,
              display: "flex",
              alignItems: "center",
              "& .inputLabel": { display: "none" },
            }}
          >
            <Box sx={{ width: 280, mt: 1.4 }}>
              <CustomCategorySelectSingle
                label=""
                name={`_add_category_${addKey}`}
                options={options}
                selectedValue=""
                placeholder="科目名を入力または選択"
                onValueChange={(v) => {
                  if (!v || addCategorySubmitting) return;
                  handleAddCategory(categoryType, revenueGroup, v);
                }}
                onAddNewOption={(v) => {
                  if (addCategorySubmitting) return;
                  handleAddCategory(categoryType, revenueGroup, v);
                }}
                labelWidth="0px"
                inputWidth="100%"
              />
            </Box>
            {/* セレクト右の ✕: 科目追加セレクトを閉じる (collapsed の「+科目を追加」に戻す) */}
            <Can action="budget:edit">
              <IconButton
                size="small"
                aria-label="科目追加をキャンセル"
                onClick={() => setAddCategoryFor(null)}
                sx={{ color: "#999", ml: 0.5, mt: 0.2, flexShrink: 0 }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Can>
          </Box>
        </TableCell>
        {displayColumns.map((m) => (
          <Fragment key={m}>
            <TableCell sx={{ ...cellSx, backgroundColor: "#FAFAFA" }} />
            <TableCell sx={{ ...cellSx, backgroundColor: "#FAFAFA" }} />
            <TableCell sx={{ ...cellSx, backgroundColor: "#FAFAFA" }} />
          </Fragment>
        ))}
      </TableRow>
    );
  };

  const renderRows = (
    rows: BudgetRow[],
    groupLabel: string,
    groupKey: string,
    // 差分色は意味的グループで決まる。サブグループ (貸室等) は親の colorKey を引き継ぐ。
    colorKey: "income" | "expense" | "capex" | "loan" = "income",
    // 指定時は科目行の代わりに 2 階層目サブグループ (貸室/その他収入/その他) を描画 (§3.1)
    subGroups?: Array<{ key: string; label: string; rows: BudgetRow[] }>,
    // 2 階層目サブグループ (貸室等) の見出し行か。true なら 1 列目を字下げする (§3.1)
    isSubGroup = false,
  ): React.ReactNode => {
    const isGroupExpanded = groupExpanded[groupKey];
    const toggleGroup = () =>
      setGroupExpanded((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
    return (
      <>
        <TableRow sx={{ cursor: "pointer", height: 38 }} onClick={toggleGroup}>
          <TableCell
            sx={{
              ...cellSx,
              // 2 階層目サブグループ (貸室/付帯/その他 等) は太字にしない。トップ(収入/支出)のみ太字。
              fontWeight: isSubGroup ? 400 : 700,
              backgroundColor: "#FAFAFA",
              position: "sticky",
              left: 0,
              zIndex: 1,
              width: vStickyWidth,
              minWidth: vStickyWidth,
              maxWidth: vStickyWidth,
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                // 2 階層目サブグループは一段字下げして親グループとの階層を示す (§3.1)
                pl: isSubGroup ? 2 : 0,
              }}
            >
              <IconButton
                size="small"
                sx={{ p: 0.25, flexShrink: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleGroup();
                }}
              >
                <KeyboardArrowRightIcon
                  sx={{
                    fontSize: 18,
                    transition: "transform 0.2s",
                    transform: isGroupExpanded
                      ? "rotate(90deg)"
                      : "rotate(0deg)",
                    color: "#3E3E3E",
                  }}
                />
              </IconButton>
              {groupLabel}
            </Box>
          </TableCell>
          {((): React.ReactNode => {
            const cells = displayColumns.map((_m, i) => {
              const tb = getTotalVal(rows, i, "budget");
              const ta = getTotalVal(rows, i, "actual");
              const diff = calcDiff(ta, tb);
              const bg = i % 2 === 1 ? "rgba(0,0,0,0.02)" : undefined;
              return (
                <Fragment key={i}>
                  <TableCell
                    sx={{
                      ...cellSx,
                      backgroundColor: bg,
                      textAlign: "center",
                      fontWeight: 700,
                      color: "#666",
                    }}
                  >
                    {fmtZero(tb)}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...cellSx,
                      backgroundColor: bg,
                      textAlign: "center",
                      fontWeight: 700,
                    }}
                  >
                    {fmtZero(ta)}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...cellSx,
                      backgroundColor: bg,
                      textAlign: "center",
                      fontWeight: 600,
                      color: diffVarianceColor(colorKey, diff),
                    }}
                  >
                    {fmtDiffZero(diff)}
                  </TableCell>
                </Fragment>
              );
            });
            return <>{cells}</>;
          })()}
        </TableRow>
        {isGroupExpanded &&
          subGroups &&
          subGroups.map((sg) => (
            <Fragment key={sg.key}>
              {renderRows(sg.rows, sg.label, sg.key, colorKey, undefined, true)}
            </Fragment>
          ))}
        {isGroupExpanded &&
          !subGroups &&
          // 「その他」も貸室/付帯と同様に科目(明細項目)行を描画する。
          // unit 紐付け入金のその他 item (例: 101 の「イベント費」) は区画明細付きで、
          // unit 非紐付け入金はカテゴリ集計 (区画明細なし) で、この科目行に積み上がる。
          rows.map((row) => {
            const isExpanded = !!expanded[row.id];
            const hasDetails = row.details && row.details.length > 0;
            // 収入/支出ともに常にアコーディオン展開可能（内訳追加のため）
            const canExpand = true;
            void hasDetails;
            return (
              <Fragment key={row.id}>
                <TableRow
                  sx={{
                    cursor: canExpand ? "pointer" : "default",
                  }}
                  onClick={() => canExpand && toggleExpand(row.id)}
                >
                  <TableCell
                    sx={{
                      ...cellSx,
                      // 科目行: サブグループ (貸室等) 配下なら一段深く字下げ (§3.1)
                      pl: isSubGroup ? 4 : 2,
                      position: "sticky",
                      left: 0,
                      zIndex: 1,
                      backgroundColor: "#FAFAFA",
                      width: vStickyWidth,
                      minWidth: vStickyWidth,
                      maxWidth: vStickyWidth,
                      boxSizing: "border-box",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        minWidth: 0,
                      }}
                    >
                      {canExpand ? (
                        <IconButton
                          size="small"
                          sx={{ p: 0.25, flexShrink: 0 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(row.id);
                          }}
                        >
                          <KeyboardArrowRightIcon
                            sx={{
                              fontSize: 18,
                              transition: "transform 0.2s",
                              transform: isExpanded
                                ? "rotate(90deg)"
                                : "rotate(0deg)",
                              color: isExpanded ? "#0B9DBD" : "#3E3E3E",
                            }}
                          />
                        </IconButton>
                      ) : (
                        <Box sx={{ width: 26, flexShrink: 0 }} />
                      )}
                      <TruncatedWithTooltip
                        text={row.category}
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  </TableCell>
                  {displayColumns.map((_, i) => {
                    const b = getVal(row, i, "budget");
                    const a = getVal(row, i, "actual");
                    const diff = calcDiff(a, b);
                    const monthKey = orderedKeys[i] || "";
                    // カテゴリ行の編集可否 (新仕様):
                    //   - 予算 / 実績ともに編集可。手動値を入れると
                    //     manual_override.{budget|actual}=true で固定され、
                    //     内訳の自動再集計で上書きされなくなる。
                    //   - 内訳合計 > 手動値 の場合は BE で 400 エラー。
                    const canEditBudget = canEditCell(
                      row,
                      monthKey,
                      fiscalYear,
                      "budget",
                      viewMode,
                    );
                    const canEditActual = canEditCell(
                      row,
                      monthKey,
                      fiscalYear,
                      "actual",
                      viewMode,
                    );
                    const bKey = makeCellKey(row.id, i, "budget");
                    const aKey = makeCellKey(row.id, i, "actual");
                    const zebraBg =
                      i % 2 === 1 ? "rgba(0,0,0,0.02)" : undefined;
                    // #4: 手動値と積み上げのバッティング (出ている間は編集不可・クリックでモーダル)
                    const bConf = getCellConflict(row, i, "budget");
                    const aConf = getCellConflict(row, i, "actual");
                    return (
                      <Fragment key={i}>
                        <TableCell
                          sx={{
                            ...cellSx,
                            position: "relative",
                            textAlign: "center",
                            color: "#999",
                            cursor:
                              bConf || canEditBudget ? "pointer" : "default",
                            backgroundColor:
                              editingCell === bKey ? "#EDF7FA" : zebraBg,
                            // 競合セルも編集セルと同じくホバーで背景色を変える。
                            ...(editingCell !== bKey && (canEditBudget || bConf)
                              ? {
                                  "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.04)",
                                  },
                                }
                              : {}),
                            // ペンは編集可かつ非競合の時だけホバーで表示。
                            ...(canEditBudget && !bConf && editingCell !== bKey
                              ? {
                                  "&:hover .edit-icon-overlay": {
                                    opacity: 1,
                                  },
                                }
                              : {}),
                          }}
                          onClick={(e) => {
                            // 月セルのクリックで親行 (TableRow) のアコーディオン
                            // 開閉が発火しないように伝播を止める
                            e.stopPropagation();
                            // バッティング中はそのセルを編集させず、確認モーダルを開く (#4)
                            if (bConf) {
                              openConflictModal(row.id, i, "budget", bConf);
                              return;
                            }
                            if (canEditBudget && editingCell !== bKey) {
                              startCellEdit(row.id, i, "budget", b);
                            }
                          }}
                        >
                          {bConf ? (
                            <>
                              {/* 競合(手動値≠積み上げ): 警告アイコンを右上(編集ペンと同位置)に常時表示。
                                  ツールチップはアイコンではなく値に出す (編集セルと同じ仕組み)。
                                  クリックで確認モーダル (セル onClick が openConflictModal)。 */}
                              <WarningAmberIcon
                                sx={{
                                  position: "absolute",
                                  top: 2,
                                  right: 2,
                                  fontSize: 14,
                                  color: "#FF9800",
                                  pointerEvents: "none",
                                }}
                              />
                              <Tooltip
                                title="クリックで選択"
                                placement="top"
                                arrow
                              >
                                <span className="cell-value">{fmtZero(b)}</span>
                              </Tooltip>
                            </>
                          ) : editingCell === bKey ? (
                            <input
                              value={editingValue}
                              onChange={(e) =>
                                setEditingValue(
                                  formatWithCommas(e.target.value),
                                )
                              }
                              onBlur={() => commitCellEdit(row.id, i, "budget")}
                              onKeyDown={(e) => {
                                if (e.key === "Enter")
                                  commitCellEdit(row.id, i, "budget");
                                if (e.key === "Escape") cancelCellEdit();
                              }}
                              autoFocus
                              style={{
                                width: "100%",
                                border: "none",
                                outline: "none",
                                background: "transparent",
                                textAlign: "center",
                                fontSize: "14px",
                                fontVariantNumeric: "tabular-nums",
                                fontFamily: "inherit",
                                color: "#999",
                                padding: 0,
                                margin: 0,
                                lineHeight: "inherit",
                              }}
                            />
                          ) : canEditBudget ? (
                            <>
                              <EditOutlinedIcon
                                className="edit-icon-overlay"
                                sx={{
                                  position: "absolute",
                                  top: 2,
                                  right: 2,
                                  fontSize: 13,
                                  color: "#666",
                                  opacity: 0,
                                  transition: "opacity 0.15s",
                                  pointerEvents: "none",
                                }}
                              />
                              <Tooltip
                                title="クリックで編集"
                                placement="top"
                                arrow
                              >
                                <span className="cell-value">{fmtZero(b)}</span>
                              </Tooltip>
                            </>
                          ) : (
                            <span className="cell-value">{fmtZero(b)}</span>
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...cellSx,
                            position: "relative",
                            textAlign: "center",
                            fontWeight: 500,
                            color: "#3E3E3E",
                            cursor:
                              aConf || canEditActual ? "pointer" : "default",
                            backgroundColor:
                              editingCell === aKey ? "#EDF7FA" : zebraBg,
                            // 競合セルも編集セルと同じくホバーで背景色を変える。
                            ...(editingCell !== aKey && (canEditActual || aConf)
                              ? {
                                  "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.04)",
                                  },
                                }
                              : {}),
                            // ペンは編集可かつ非競合の時だけホバーで表示。
                            ...(canEditActual && !aConf && editingCell !== aKey
                              ? {
                                  "&:hover .edit-icon-overlay": {
                                    opacity: 1,
                                  },
                                }
                              : {}),
                          }}
                          onClick={(e) => {
                            // 月セルのクリックで親行のアコーディオン開閉が発火しないよう停止
                            e.stopPropagation();
                            // バッティング中はそのセルを編集させず、確認モーダルを開く (#4)
                            if (aConf) {
                              openConflictModal(row.id, i, "actual", aConf);
                              return;
                            }
                            if (canEditActual && editingCell !== aKey) {
                              startCellEdit(row.id, i, "actual", a);
                            }
                          }}
                        >
                          {aConf ? (
                            <>
                              {/* 競合(手動値≠積み上げ): 警告アイコンを右上(編集ペンと同位置)に常時表示。
                                  ツールチップはアイコンではなく値に出す (編集セルと同じ仕組み)。 */}
                              <WarningAmberIcon
                                sx={{
                                  position: "absolute",
                                  top: 2,
                                  right: 2,
                                  fontSize: 14,
                                  color: "#FF9800",
                                  pointerEvents: "none",
                                }}
                              />
                              <Tooltip
                                title="クリックで選択"
                                placement="top"
                                arrow
                              >
                                <span className="cell-value">{fmtZero(a)}</span>
                              </Tooltip>
                            </>
                          ) : editingCell === aKey ? (
                            <input
                              value={editingValue}
                              onChange={(e) =>
                                setEditingValue(
                                  formatWithCommas(e.target.value),
                                )
                              }
                              onBlur={() => commitCellEdit(row.id, i, "actual")}
                              onKeyDown={(e) => {
                                if (e.key === "Enter")
                                  commitCellEdit(row.id, i, "actual");
                                if (e.key === "Escape") cancelCellEdit();
                              }}
                              autoFocus
                              style={{
                                width: "100%",
                                border: "none",
                                outline: "none",
                                background: "transparent",
                                textAlign: "center",
                                fontSize: "14px",
                                fontVariantNumeric: "tabular-nums",
                                fontFamily: "inherit",
                                color: "#3E3E3E",
                                fontWeight: 500,
                                padding: 0,
                                margin: 0,
                                lineHeight: "inherit",
                              }}
                            />
                          ) : canEditActual ? (
                            <>
                              <EditOutlinedIcon
                                className="edit-icon-overlay"
                                sx={{
                                  position: "absolute",
                                  top: 2,
                                  right: 2,
                                  fontSize: 13,
                                  color: "#666",
                                  opacity: 0,
                                  transition: "opacity 0.15s",
                                  pointerEvents: "none",
                                }}
                              />
                              <Tooltip
                                title="クリックで編集"
                                placement="top"
                                arrow
                              >
                                <span className="cell-value">{fmtZero(a)}</span>
                              </Tooltip>
                            </>
                          ) : (
                            <span className="cell-value">{fmtZero(a)}</span>
                          )}
                        </TableCell>
                        {/* 差異セル (クリックでアコーディオン開閉が発火しないよう停止) */}
                        <TableCell
                          sx={{
                            ...cellSx,
                            textAlign: "center",
                            backgroundColor: zebraBg,
                            color: diffVarianceColor(colorKey, diff),
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {fmtDiffZero(diff)}
                        </TableCell>
                      </Fragment>
                    );
                  })}
                </TableRow>
                {hasDetails &&
                  row.details!.map((detail) => (
                    <TableRow
                      key={`${row.id}-${detail.id}`}
                      sx={{ display: isExpanded ? "table-row" : "none" }}
                    >
                      <TableCell
                        sx={{
                          ...cellSx,
                          // 明細行: サブグループ配下なら科目行ぶん深く字下げ (§3.1)。
                          // +4px (0.5) 右に寄せて科目行との段差を明確化。
                          pl: isSubGroup ? "60px" : "44px",
                          position: "sticky",
                          left: 0,
                          zIndex: 1,
                          backgroundColor: "#FAFAFA",
                          fontSize: "14px",
                          color: "#666",
                          width: vStickyWidth,
                          minWidth: vStickyWidth,
                          maxWidth: vStickyWidth,
                          boxSizing: "border-box",
                          overflow: "hidden",
                        }}
                      >
                        <Box sx={{ display: "flex", minWidth: 0 }}>
                          {row.group === "income" &&
                          detail.sectionId &&
                          (detail.revenueType === "rent" ||
                            detail.revenueType === "ancillary_income") ? (
                            <TruncatedWithTooltip
                              text={detail.label}
                              sx={{
                                color: "inherit",
                                cursor: "pointer",
                                textDecoration: "underline",
                                flex: 1,
                                "&:hover": { color: "#0B9DBD" },
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setPreviewSectionId(detail.sectionId!);
                              }}
                            />
                          ) : (row.group === "expense" ||
                              row.group === "expense_opex" ||
                              row.group === "expense_capex") &&
                            detail.id ? (
                            <TruncatedWithTooltip
                              text={detail.label}
                              sx={{
                                color: "inherit",
                                cursor: "pointer",
                                textDecoration: "underline",
                                flex: 1,
                                "&:hover": { color: "#0B9DBD" },
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setPreviewExpenseId(detail.id);
                              }}
                            />
                          ) : (
                            <TruncatedWithTooltip
                              text={detail.label}
                              sx={{ flex: 1 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      {displayColumns.map((_, i) => {
                        const b = getDetailVal(detail, i, "budget");
                        const a = getDetailVal(detail, i, "actual");
                        const diff = calcDiff(a, b);
                        const dMonthKey = orderedKeys[i] || "";
                        const canEditDetailBudget = canEditBudgetDetailCell({
                          row,
                          detail,
                          monthIndex: i,
                          monthKey: dMonthKey,
                          fiscalYear,
                          field: "budget",
                          viewMode,
                        });
                        const canEditDetailActual = canEditBudgetDetailCell({
                          row,
                          detail,
                          monthIndex: i,
                          monthKey: dMonthKey,
                          fiscalYear,
                          field: "actual",
                          viewMode,
                        });
                        // 項目(内訳)の競合: 手動実績(予実) ≠ 入金管理。a は手動実績優先で表示中。
                        // 個別支出行は発生日月以外を触れないため、競合モーダルも同じ月だけに限定する。
                        const aConf = canEditDetailActual
                          ? getDetailConflict(detail, i)
                          : null;
                        const detailId = detail.id;
                        const dbKey = makeCellKey(
                          row.id,
                          i,
                          "budget",
                          detailId,
                        );
                        const daKey = makeCellKey(
                          row.id,
                          i,
                          "actual",
                          detailId,
                        );
                        const detailZebraBg =
                          i % 2 === 1 ? "rgba(0,0,0,0.02)" : undefined;
                        return (
                          <Fragment key={i}>
                            <TableCell
                              sx={{
                                ...cellSx,
                                position: "relative",
                                textAlign: "center",
                                color: "#bbb",
                                fontSize: "14px",
                                backgroundColor:
                                  editingCell === dbKey
                                    ? "#EDF7FA"
                                    : detailZebraBg,
                                cursor: canEditDetailBudget
                                  ? "pointer"
                                  : "default",
                                ...(canEditDetailBudget && editingCell !== dbKey
                                  ? {
                                      "&:hover": {
                                        backgroundColor: "rgba(0,0,0,0.04)",
                                      },
                                      "&:hover .edit-icon-overlay": {
                                        opacity: 1,
                                      },
                                    }
                                  : {}),
                              }}
                              onClick={() =>
                                canEditDetailBudget &&
                                editingCell !== dbKey &&
                                startCellEdit(row.id, i, "budget", b, detailId)
                              }
                            >
                              {editingCell === dbKey ? (
                                <input
                                  value={editingValue}
                                  onChange={(e) =>
                                    setEditingValue(
                                      formatWithCommas(e.target.value),
                                    )
                                  }
                                  onBlur={() =>
                                    commitDetailCellEdit(
                                      row.id,
                                      detailId,
                                      i,
                                      "budget",
                                    )
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter")
                                      commitDetailCellEdit(
                                        row.id,
                                        detailId,
                                        i,
                                        "budget",
                                      );
                                    if (e.key === "Escape") cancelCellEdit();
                                  }}
                                  autoFocus
                                  style={{
                                    width: "100%",
                                    border: "none",
                                    outline: "none",
                                    background: "transparent",
                                    textAlign: "center",
                                    fontSize: "14px",
                                    fontVariantNumeric: "tabular-nums",
                                    fontFamily: "inherit",
                                    color: "#bbb",
                                    padding: 0,
                                    margin: 0,
                                    lineHeight: "inherit",
                                  }}
                                />
                              ) : canEditDetailBudget ? (
                                <>
                                  <EditOutlinedIcon
                                    className="edit-icon-overlay"
                                    sx={{
                                      position: "absolute",
                                      top: 2,
                                      right: 2,
                                      fontSize: 12,
                                      color: "#666",
                                      opacity: 0,
                                      transition: "opacity 0.15s",
                                      pointerEvents: "none",
                                    }}
                                  />
                                  <Tooltip
                                    title="クリックで編集"
                                    placement="top"
                                    arrow
                                  >
                                    <span className="cell-value">{fmt(b)}</span>
                                  </Tooltip>
                                </>
                              ) : (
                                <span className="cell-value">{fmt(b)}</span>
                              )}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellSx,
                                position: "relative",
                                textAlign: "center",
                                fontSize: "14px",
                                backgroundColor:
                                  editingCell === daKey
                                    ? "#EDF7FA"
                                    : detailZebraBg,
                                color: "#666",
                                cursor:
                                  aConf || canEditDetailActual
                                    ? "pointer"
                                    : "default",
                                ...((canEditDetailActual || aConf) &&
                                editingCell !== daKey
                                  ? {
                                      "&:hover": {
                                        backgroundColor: "rgba(0,0,0,0.04)",
                                      },
                                      "&:hover .edit-icon-overlay": {
                                        opacity: 1,
                                      },
                                    }
                                  : {}),
                              }}
                              onClick={() => {
                                if (aConf) {
                                  openDetailConflictModal(
                                    row.id,
                                    i,
                                    detail.label,
                                    aConf,
                                  );
                                  return;
                                }
                                if (
                                  canEditDetailActual &&
                                  editingCell !== daKey
                                )
                                  startCellEdit(
                                    row.id,
                                    i,
                                    "actual",
                                    a,
                                    detailId,
                                  );
                              }}
                            >
                              {editingCell === daKey ? (
                                <input
                                  value={editingValue}
                                  onChange={(e) =>
                                    setEditingValue(
                                      formatWithCommas(e.target.value),
                                    )
                                  }
                                  onBlur={() =>
                                    commitDetailCellEdit(
                                      row.id,
                                      detailId,
                                      i,
                                      "actual",
                                    )
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter")
                                      commitDetailCellEdit(
                                        row.id,
                                        detailId,
                                        i,
                                        "actual",
                                      );
                                    if (e.key === "Escape") cancelCellEdit();
                                  }}
                                  autoFocus
                                  style={{
                                    width: "100%",
                                    border: "none",
                                    outline: "none",
                                    background: "transparent",
                                    textAlign: "center",
                                    fontSize: "14px",
                                    fontVariantNumeric: "tabular-nums",
                                    fontFamily: "inherit",
                                    color: "#666",
                                    padding: 0,
                                    margin: 0,
                                    lineHeight: "inherit",
                                  }}
                                />
                              ) : aConf ? (
                                <>
                                  {/* 項目競合(手動実績≠入金管理): ⚠️ を右上に。クリックで確認モーダル。 */}
                                  <WarningAmberIcon
                                    sx={{
                                      position: "absolute",
                                      top: 2,
                                      right: 2,
                                      fontSize: 14,
                                      color: "#FF9800",
                                      pointerEvents: "none",
                                    }}
                                  />
                                  <Tooltip
                                    title="クリックで確認"
                                    placement="top"
                                    arrow
                                  >
                                    <span className="cell-value">{fmt(a)}</span>
                                  </Tooltip>
                                </>
                              ) : canEditDetailActual ? (
                                <>
                                  <EditOutlinedIcon
                                    className="edit-icon-overlay"
                                    sx={{
                                      position: "absolute",
                                      top: 2,
                                      right: 2,
                                      fontSize: 12,
                                      color: "#666",
                                      opacity: 0,
                                      transition: "opacity 0.15s",
                                      pointerEvents: "none",
                                    }}
                                  />
                                  <Tooltip
                                    title="クリックで編集"
                                    placement="top"
                                    arrow
                                  >
                                    <span className="cell-value">{fmt(a)}</span>
                                  </Tooltip>
                                </>
                              ) : (
                                <span className="cell-value">{fmt(a)}</span>
                              )}
                            </TableCell>
                            {/* detail差異セル */}
                            <TableCell
                              sx={{
                                ...cellSx,
                                textAlign: "center",
                                backgroundColor: detailZebraBg,
                                color: diffVarianceColor(colorKey, diff),
                              }}
                            >
                              {fmtDiff(diff)}
                            </TableCell>
                          </Fragment>
                        );
                      })}
                    </TableRow>
                  ))}
                {/* カテゴリ配下の追加行 (展開時)
                  支出: ExpenseDrawer を開いて ExpenseRecord を作成する
                  収入: PaymentRecord 連動はまだ未実装のため、現状は導線のみ表示
                       (TODO: PaymentDrawer or 区画選択モーダルを開く) */}
                {isExpanded && (
                  <TableRow
                    sx={{
                      backgroundColor: "#FAFAFA",
                      height: 44,
                      display: isExpanded ? "table-row" : "none",
                    }}
                  >
                    <TableCell
                      sx={{
                        ...cellSx,
                        py: 0.5,
                        // 内訳階層のインデント。収入(名称追加)は56px、支出(支出追加)は40px。
                        pl: row.group === "income" ? "56px" : "40px",
                        position: "sticky",
                        left: 0,
                        zIndex: 1,
                        backgroundColor: "#FAFAFA",
                        fontSize: "12px",
                        width: vStickyWidth,
                        minWidth: vStickyWidth,
                        maxWidth: vStickyWidth,
                        boxSizing: "border-box",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          if (row.group === "income") {
                            // 「その他」(revenue_group=other) は契約(Unit)に紐づかない
                            // 収入も吸収する。階層は カテゴリ>科目 までで Unit を持たない
                            // ため、収入タブと同じ収入登録モーダル (PaymentRecord 直登録)
                            // を開く。
                            const rg = row.categoryId
                              ? revenueGroupById.get(row.categoryId)
                              : undefined;
                            if (rg === "other") {
                              setIncomePaymentOpen(true);
                              return;
                            }
                            // 貸室 / その他収入 (stock 系) の「+収入追加」=
                            // 物件構成への新規 Unit 登録モーダルを開く導線。
                            // - 賃料 / 共益費 → 専有部固定で開く
                            // - その他収入 → area_type 未指定でユーザー選択
                            const inferredFromDetails = row.details?.find(
                              (d) => !!d.revenueType,
                            )?.revenueType;
                            const isRent =
                              inferredFromDetails === "rent" ||
                              row.category === "賃料" ||
                              row.category === "共益費";
                            setIncomeAddModal({
                              kind: isRent ? "private_area" : "ancillary",
                            });
                            return;
                          }
                          // 支出: ExpenseDrawer を開く
                          const record = list.data.find(
                            (r: any) => r.sort_key === row.id,
                          );
                          setExpenseDrawer({
                            open: true,
                            rowCategory: row.category,
                            rowCategoryId: record?.category_id,
                            rowGroup: row.group,
                          });
                        }}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          cursor: "pointer",
                          color: "#999",
                          "&:hover": { color: "#0B9DBD" },
                        }}
                      >
                        <AddIcon sx={{ fontSize: 16 }} />
                        <Typography
                          sx={{ fontSize: "12px", whiteSpace: "nowrap" }}
                        >
                          {row.group === "income" ? "名称追加" : "支出追加"}
                        </Typography>
                      </Box>
                    </TableCell>
                    {displayColumns.map((m) => (
                      <Fragment key={m}>
                        <TableCell
                          sx={{ ...cellSx, backgroundColor: "#FAFAFA" }}
                        />
                        <TableCell
                          sx={{ ...cellSx, backgroundColor: "#FAFAFA" }}
                        />
                        <TableCell
                          sx={{ ...cellSx, backgroundColor: "#FAFAFA" }}
                        />
                      </Fragment>
                    ))}
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        {/* 「その他」配下の入金 (unit 紐付け/非紐付けとも) は科目(明細項目)行の actual に
            集約して表示する (上の rows.map)。旧「1入金=1行」の専用描画は廃止
            (unit 紐付け入金のその他 item が描画されず合計だけ増える不整合への対処)。 */}
        {/* 「+科目を追加」行 (新方式 §3.3)。
            収入サブグループ (貸室/その他収入/その他) と 支出/CAPEX グループの配下に出す。 */}
        {isGroupExpanded &&
          !subGroups &&
          (() => {
            // groupKey から category_type / revenue_group を導出
            if (groupKey === "income_stock_rent") {
              return renderAddCategoryRow(
                groupKey,
                "income",
                "stock_rent",
                isSubGroup ? 4 : 2,
              );
            }
            if (groupKey === "income_stock_other") {
              return renderAddCategoryRow(
                groupKey,
                "income",
                "stock_other",
                isSubGroup ? 4 : 2,
              );
            }
            if (groupKey === "income_other") {
              // その他も貸室/付帯と同様に「明細項目追加」(科目追加) できる。
              // 加えて契約非紐付けの実収入を登録する「収入追加」(収入タブと同じモーダル) も残す。
              return (
                <>
                  {renderAddCategoryRow(
                    groupKey,
                    "income",
                    "other",
                    isSubGroup ? 4 : 2,
                  )}
                  <TableRow sx={{ backgroundColor: "#FAFAFA", height: 44 }}>
                    <TableCell
                      sx={{
                        ...cellSx,
                        py: 0.5,
                        pl: isSubGroup ? 4 : 2,
                        position: "sticky",
                        left: 0,
                        zIndex: 1,
                        backgroundColor: "#FAFAFA",
                        width: vStickyWidth,
                        minWidth: vStickyWidth,
                        maxWidth: vStickyWidth,
                        boxSizing: "border-box",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          cursor: "pointer",
                          color: "#999",
                          "&:hover": { color: "#0B9DBD" },
                        }}
                        onClick={() => setIncomePaymentOpen(true)}
                      >
                        <AddIcon sx={{ fontSize: 16 }} />
                        <Typography
                          sx={{ fontSize: "12px", whiteSpace: "nowrap" }}
                        >
                          収入追加
                        </Typography>
                      </Box>
                    </TableCell>
                    {displayColumns.map((m) => (
                      <Fragment key={m}>
                        <TableCell
                          sx={{ ...cellSx, backgroundColor: "#FAFAFA" }}
                        />
                        <TableCell
                          sx={{ ...cellSx, backgroundColor: "#FAFAFA" }}
                        />
                        <TableCell
                          sx={{ ...cellSx, backgroundColor: "#FAFAFA" }}
                        />
                      </Fragment>
                    ))}
                  </TableRow>
                </>
              );
            }
            // 支出(OPEX/CAPEX)は明細項目(科目)の追加に未対応のため、追加行を出さない。
            if (groupKey === "expense" || groupKey === "capex") {
              return null;
            }
            return null;
          })()}
      </>
    );
  };

  const renderAddExpenseRow = (
    targetGroup: "expense_opex" | "expense_capex" = "expense_opex",
  ) => {
    const adding = addingExpense;
    const setAdding = setAddingExpense;
    const selected = newExpenseCategoryId;
    const setSelected = setNewExpenseCategoryId;
    // 支出カテゴリは設定済みの科目マスタから選択する。
    // 同 category_id の BudgetRecord が既にグループにある場合は除外
    // (同じ「工事費」を OPEX と CAPEX 両方に追加できる)
    const groupRowsForFilter =
      targetGroup === "expense_capex" ? capexRows : opexRows;
    const existingInGroup = new Set(
      groupRowsForFilter
        .map((r) => r.id?.split("#").pop())
        .filter(Boolean) as string[],
    );
    const availableCategories = categories.data.filter(
      (c) =>
        (c.category_type === "expense" ||
          c.category_type === "expense_opex" ||
          c.category_type === "expense_capex") &&
        !existingInGroup.has(c.category_id),
    );

    if (!adding) {
      return (
        <TableRow sx={{ backgroundColor: "#FAFAFA" }}>
          <TableCell
            sx={{
              ...cellSx,
              py: 1,
              pl: "20px",
              position: "sticky",
              left: 0,
              zIndex: 1,
              backgroundColor: "#FAFAFA",
              width: vStickyWidth,
              minWidth: vStickyWidth,
              maxWidth: vStickyWidth,
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
                color: "#999",
                "&:hover": { color: "#0B9DBD" },
              }}
              onClick={() => setAdding(true)}
            >
              <AddIcon sx={{ fontSize: 16 }} />
              <Typography sx={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                明細項目追加
              </Typography>
            </Box>
          </TableCell>
          {displayColumns.map((m) => (
            <Fragment key={m}>
              <TableCell sx={{ ...cellSx, backgroundColor: "#FAFAFA" }} />
              <TableCell sx={{ ...cellSx, backgroundColor: "#FAFAFA" }} />
              <TableCell sx={{ ...cellSx, backgroundColor: "#FAFAFA" }} />
            </Fragment>
          ))}
        </TableRow>
      );
    }

    return (
      <TableRow sx={{ backgroundColor: "#FAFAFA" }}>
        <TableCell
          sx={{
            ...cellSx,
            position: "sticky",
            left: 0,
            zIndex: 5,
            backgroundColor: "#FAFAFA",
            py: 1,
            width: vStickyWidth,
            minWidth: vStickyWidth,
            maxWidth: vStickyWidth,
            boxSizing: "border-box",
            overflow: "visible",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              pl: 1,
              width: 0,
              overflow: "visible",
              whiteSpace: "nowrap",
              "& > *": { flexShrink: 0 },
            }}
          >
            <MuiSelect
              size="small"
              value={selected}
              displayEmpty
              IconComponent={DropDownArrowIcon}
              onChange={(e) => {
                setSelected(e.target.value);
              }}
              renderValue={(value) => {
                if (!value)
                  return (
                    <span style={{ color: "#989898" }}>科目を選択...</span>
                  );
                const cat = availableCategories.find(
                  (c) => c.category_id === value,
                );
                return <span>{cat?.name || value}</span>;
              }}
              sx={{
                minWidth: 160,
                height: "34px",
                fontSize: "12px",
                backgroundColor: "#ffffff",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D9D9D9",
                  borderRadius: "8px",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D9D9D9",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D9D9D9",
                  borderWidth: "1px",
                },
                ".MuiSelect-icon": {
                  fontSize: "14px",
                  top: "calc(50%)",
                  transform: "translateY(-50%)",
                  right: "12px !important",
                },
                "& .MuiSelect-iconOpen": {
                  transform: "translateY(-50%) rotate(180deg) !important",
                },
                "& .MuiSelect-select": {
                  fontSize: "12px",
                },
              }}
            >
              <MenuItem
                value=""
                disabled
                sx={{ fontSize: "12px", color: "#989898", padding: "8px 16px" }}
              >
                科目を選択...
              </MenuItem>
              {availableCategories.map((c) => (
                <MenuItem
                  key={c.category_id}
                  value={c.category_id}
                  sx={{ fontSize: "12px", padding: "8px 16px" }}
                >
                  {c.name}
                </MenuItem>
              ))}
            </MuiSelect>
            <IconButton
              size="small"
              onClick={() => handleInlineAdd(targetGroup)}
              disabled={addingSubmitting || !selected}
              sx={{ color: "#0B9DBD" }}
            >
              <CheckIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => {
                setAdding(false);
                setSelected("");
              }}
              sx={{ color: "#999" }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </TableCell>
        {displayColumns.map((m) => (
          <Fragment key={m}>
            <TableCell
              sx={{
                ...cellSx,
                backgroundColor: "#FAFAFA",
                position: "relative",
                zIndex: 4,
              }}
            />
            <TableCell
              sx={{
                ...cellSx,
                backgroundColor: "#FAFAFA",
                position: "relative",
                zIndex: 4,
              }}
            />
            <TableCell
              sx={{
                ...cellSx,
                backgroundColor: "#FAFAFA",
                position: "relative",
                zIndex: 4,
              }}
            />
          </Fragment>
        ))}
      </TableRow>
    );
  };

  const renderAddIncomeRow = () => {
    const adding = addingIncome;
    const setAdding = setAddingIncome;
    const selected = newIncomeCategoryId;
    const setSelected = setNewIncomeCategoryId;
    const seenNames = new Set<string>();
    // 収入カテゴリは設定済みの科目マスタから選択する。
    const availableCategories = categories.data
      .filter(
        (c) =>
          c.category_type === "income" &&
          !existingCategoryIds.has(c.category_id),
      )
      .filter((c) => {
        if (seenNames.has(c.name)) return false;
        seenNames.add(c.name);
        return true;
      });

    if (!adding) {
      return (
        <TableRow sx={{ backgroundColor: "#FAFAFA" }}>
          <TableCell
            sx={{
              ...cellSx,
              py: 1,
              pl: "20px",
              position: "sticky",
              left: 0,
              zIndex: 1,
              backgroundColor: "#FAFAFA",
              width: vStickyWidth,
              minWidth: vStickyWidth,
              maxWidth: vStickyWidth,
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
                color: "#999",
                "&:hover": { color: "#0B9DBD" },
              }}
              onClick={() => setAdding(true)}
            >
              <AddIcon sx={{ fontSize: 16 }} />
              <Typography sx={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                明細項目追加
              </Typography>
            </Box>
          </TableCell>
          {displayColumns.map((m) => (
            <Fragment key={m}>
              <TableCell sx={{ ...cellSx, backgroundColor: "#FAFAFA" }} />
              <TableCell sx={{ ...cellSx, backgroundColor: "#FAFAFA" }} />
              <TableCell sx={{ ...cellSx, backgroundColor: "#FAFAFA" }} />
            </Fragment>
          ))}
        </TableRow>
      );
    }

    return (
      <TableRow sx={{ backgroundColor: "#FAFAFA" }}>
        <TableCell
          sx={{
            ...cellSx,
            position: "sticky",
            left: 0,
            zIndex: 5,
            backgroundColor: "#FAFAFA",
            py: 1,
            width: vStickyWidth,
            minWidth: vStickyWidth,
            maxWidth: vStickyWidth,
            boxSizing: "border-box",
            overflow: "visible",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              pl: 1,
              width: 0,
              overflow: "visible",
              whiteSpace: "nowrap",
              "& > *": { flexShrink: 0 },
            }}
          >
            <MuiSelect
              size="small"
              value={selected}
              displayEmpty
              IconComponent={DropDownArrowIcon}
              onChange={(e) => {
                setSelected(e.target.value as string);
              }}
              renderValue={(value) => {
                if (!value)
                  return (
                    <span style={{ color: "#989898" }}>科目を選択...</span>
                  );
                const cat = availableCategories.find(
                  (c) => c.category_id === value,
                );
                return <span>{cat?.name || value}</span>;
              }}
              sx={{
                minWidth: 160,
                height: "34px",
                fontSize: "12px",
                backgroundColor: "#ffffff",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D9D9D9",
                  borderRadius: "8px",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D9D9D9",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D9D9D9",
                  borderWidth: "1px",
                },
                ".MuiSelect-icon": {
                  fontSize: "14px",
                  top: "calc(50%)",
                  transform: "translateY(-50%)",
                  right: "12px !important",
                },
                "& .MuiSelect-iconOpen": {
                  transform: "translateY(-50%) rotate(180deg) !important",
                },
                "& .MuiSelect-select": {
                  fontSize: "12px",
                },
              }}
            >
              <MenuItem
                value=""
                disabled
                sx={{ fontSize: "12px", color: "#989898", padding: "8px 16px" }}
              >
                科目を選択...
              </MenuItem>
              {availableCategories.map((c) => (
                <MenuItem
                  key={c.category_id}
                  value={c.category_id}
                  sx={{ fontSize: "12px", padding: "8px 16px" }}
                >
                  {c.name}
                </MenuItem>
              ))}
            </MuiSelect>
            <IconButton
              size="small"
              onClick={async () => {
                const categoryId = selected;
                if (!categoryId) {
                  showToast({
                    message: "科目を選択してください",
                    type: "error",
                  });
                  return;
                }
                setAddingIncomeSubmitting(true);
                try {
                  await dispatch(
                    createBudget({
                      property_id: propertyId,
                      fiscal_year: fiscalYear,
                      category_id: categoryId,
                      months: buildEmptyMonths(orderedKeys),
                      source: "manual",
                    }),
                  ).unwrap();
                  setAdding(false);
                  setSelected("");
                } catch {
                  showToast({ message: "追加に失敗しました", type: "error" });
                } finally {
                  setAddingIncomeSubmitting(false);
                }
              }}
              disabled={addingIncomeSubmitting || !selected}
              sx={{ color: "#0B9DBD" }}
            >
              <CheckIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => {
                setAdding(false);
                setSelected("");
              }}
              sx={{ color: "#888" }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </TableCell>
        {displayColumns.map((m) => (
          <Fragment key={m}>
            <TableCell
              sx={{
                ...cellSx,
                backgroundColor: "#FAFAFA",
                position: "relative",
                zIndex: 4,
              }}
            />
            <TableCell
              sx={{
                ...cellSx,
                backgroundColor: "#FAFAFA",
                position: "relative",
                zIndex: 4,
              }}
            />
            <TableCell
              sx={{
                ...cellSx,
                backgroundColor: "#FAFAFA",
                position: "relative",
                zIndex: 4,
              }}
            />
          </Fragment>
        ))}
      </TableRow>
    );
  };

  // 「+カテゴリ追加」関連 (収入 / 支出) は現在 UI から切り離してあるが、
  // 将来復活させる可能性があるため定義はそのまま残す。
  // tsc の noUnusedLocals 回避のため void 参照を入れておく。
  void renderAddIncomeRow;
  void renderAddExpenseRow;
  void handleInlineAdd;

  // ── CSV出力 ── TODO: 実装後にコメント解除
  /* const handleCsvExport = useCallback(() => {
    const safeName = (propertyName || "物件").replace(/[\\/:*?"<>|]/g, "_");
    const viewLabel = viewMode === "monthly" ? "月次" : "四半期";
    const fileName = `予実_${safeName}_${fiscalYear}年度_${viewLabel}.csv`;

    // ヘッダー行
    const headerCols = ["区分", "科目", "予実"];
    for (const col of displayColumns) {
      headerCols.push(col);
    }
    headerCols.push("年度合計");

    const rows: string[][] = [];
    rows.push(headerCols);

    const buildRows = (
      budgetRows: BudgetRow[],
      groupLabel: string,
    ) => {
      for (const row of budgetRows) {
        // 予算行
        const budgetCells = [groupLabel, row.category, "予算"];
        let budgetTotal = 0;
        for (let i = 0; i < displayColumns.length; i++) {
          const v = getVal(row, i, "budget");
          budgetTotal += v;
          budgetCells.push(String(v));
        }
        budgetCells.push(String(budgetTotal));
        rows.push(budgetCells);

        // 実績行
        const actualCells = [groupLabel, row.category, "実績"];
        let actualTotal = 0;
        for (let i = 0; i < displayColumns.length; i++) {
          const v = getVal(row, i, "actual");
          actualTotal += v;
          actualCells.push(String(v));
        }
        actualCells.push(String(actualTotal));
        rows.push(actualCells);

        // 差異行
        const diffCells = [groupLabel, row.category, "差異"];
        let diffTotal = 0;
        for (let i = 0; i < displayColumns.length; i++) {
          const b = getVal(row, i, "budget");
          const a = getVal(row, i, "actual");
          const d = a - b;
          diffTotal += d;
          diffCells.push(String(d));
        }
        diffCells.push(String(diffTotal));
        rows.push(diffCells);

        // 詳細行（内訳）
        if (row.details) {
          for (const detail of row.details) {
            const detLabel = `${row.category}（${detail.label}）`;
            const detBudget = [groupLabel, detLabel, "予算"];
            const detActual = [groupLabel, detLabel, "実績"];
            let detBTotal = 0;
            let detATotal = 0;
            for (let i = 0; i < displayColumns.length; i++) {
              const bv = getDetailVal(detail, i, "budget");
              const av = getDetailVal(detail, i, "actual");
              detBTotal += bv;
              detATotal += av;
              detBudget.push(String(bv));
              detActual.push(String(av));
            }
            detBudget.push(String(detBTotal));
            detActual.push(String(detATotal));
            rows.push(detBudget);
            rows.push(detActual);
          }
        }
      }

      // 小計行
      const subBudget = [groupLabel, `${groupLabel}合計`, "予算"];
      const subActual = [groupLabel, `${groupLabel}合計`, "実績"];
      let subBTotal = 0;
      let subATotal = 0;
      for (let i = 0; i < displayColumns.length; i++) {
        const bv = getTotalVal(budgetRows, i, "budget");
        const av = getTotalVal(budgetRows, i, "actual");
        subBTotal += bv;
        subATotal += av;
        subBudget.push(String(bv));
        subActual.push(String(av));
      }
      subBudget.push(String(subBTotal));
      subActual.push(String(subATotal));
      rows.push(subBudget);
      rows.push(subActual);
    };

    buildRows(incomeRows, "収入");
    rows.push([]); // 空行
    buildRows(expenseRows, "支出");

    // NOI 行
    rows.push([]);
    const noiBudget = ["", "NOI (収入-支出)", "予算"];
    const noiActual = ["", "NOI (収入-支出)", "実績"];
    let noiBTotal = 0;
    let noiATotal = 0;
    for (let i = 0; i < displayColumns.length; i++) {
      const ib = getTotalVal(incomeRows, i, "budget");
      const ia = getTotalVal(incomeRows, i, "actual");
      const eb = getTotalVal(expenseRows, i, "budget");
      const ea = getTotalVal(expenseRows, i, "actual");
      noiBTotal += ib - eb;
      noiATotal += ia - ea;
      noiBudget.push(String(ib - eb));
      noiActual.push(String(ia - ea));
    }
    noiBudget.push(String(noiBTotal));
    noiActual.push(String(noiATotal));
    rows.push(noiBudget);
    rows.push(noiActual);

    // CSV文字列生成 (BOM付きUTF-8)
    const csvContent = rows
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
      )
      .join("\r\n");
    const bom = "\uFEFF";
    const blob = new Blob([bom + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }, [
    propertyName,
    fiscalYear,
    viewMode,
    displayColumns,
    incomeRows,
    expenseRows,
    getVal,
    getDetailVal,
    getTotalVal,
  ]); */

  const hasBudgetCache = Boolean(list.loaded);
  useStaleInitialLoadErrorToast({
    errorType: initialLoadError,
    hasCachedData: hasBudgetCache,
    dedupeKey: `budget:${propertyId}:${fiscalYear}`,
  });

  if (shouldShowInitialLoadError(initialLoadError, hasBudgetCache)) {
    return (
      <InitialLoadError errorType={initialLoadError} onRetry={fetchDetail} />
    );
  }

  return (
    <Box sx={{ mb: 0, pb: 5 }}>
      <Box sx={{ mb: 2 }}>
        <CustomSectionTitle title="予実管理" addBorder />
      </Box>
      {/* 年度切替: セクションタイトル直下に独立配置。変更で即時 API fetch */}
      <Box sx={{ mt: 2, mb: 1, width: 140 }}>
        <CustomSingleSelect
          label=""
          name="fiscalYear"
          control={control}
          errors={{}}
          inputWidth="100%"
          inputWidthSp="100%"
          options={FISCAL_YEAR_OPTIONS}
          onChangeValue={(v) => {
            setBudgetTableLoading(true);
            setFiscalYear(String(v));
          }}
          hasSpacing={false}
        />
      </Box>

      {/* サマリーカード */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          // 金額が長いカードだけ内容に追従して広がる (均等割りにしない)。
          // 各カードは内容幅、入り切らなければ次行へ折返す。
          gap: 1,
          mb: 1,
          "& > *": { flex: "0 0 auto", minWidth: "160px" },
          "& .summary-card-value": { whiteSpace: "nowrap" },
        }}
      >
        <SummaryCard
          label="収入合計"
          labelSize="12px"
          value={fmtZero(ytd.ia)}
          subAlign="flex-start"
          loading={budgetTableLoading}
          loadingDelayMs={150}
          sub={
            <Stack spacing={0.5}>
              <Typography sx={{ fontSize: "12px", color: "#999" }}>
                予算: {fmtZero(ytd.ib)}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min(ytd.ib > 0 ? (ytd.ia / ytd.ib) * 100 : 0, 100)}
                sx={{ height: 6, borderRadius: 1 }}
              />
            </Stack>
          }
        />
        <SummaryCard
          label="支出合計"
          labelSize="12px"
          value={fmtZero(ytd.expTotalActual)}
          subAlign="flex-start"
          loading={budgetTableLoading}
          loadingDelayMs={150}
          sub={
            <Stack spacing={0.5}>
              <Typography sx={{ fontSize: "12px", color: "#999" }}>
                予算: {fmtZero(ytd.expTotalBudget)}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min(
                  ytd.expTotalBudget > 0
                    ? (ytd.expTotalActual / ytd.expTotalBudget) * 100
                    : 0,
                  100,
                )}
                sx={{
                  height: 6,
                  borderRadius: 1,
                  "& .MuiLinearProgress-bar": { backgroundColor: "#BDBDBD" },
                  backgroundColor: "#E0E0E0",
                }}
              />
            </Stack>
          }
        />
        <SummaryCard
          label="NOI"
          labelSize="12px"
          sx={{ backgroundColor: "#E8F4FD" }}
          value={fmtZero(ytd.noiActual)}
          valueColor={
            ytd.noiActual >= 0 ? colors.text.primary : colors.status.error
          }
          loading={budgetTableLoading}
          loadingDelayMs={150}
        />
        {/* PHASE 2: NCF サマリーカードは収支の融資部分リリース時に復活
        <SummaryCard
          label="NCF (年度累計)"
          labelSize="12px"
          sx={{ backgroundColor: "#E8F5E9" }}
          icon={
            ytd.ncfActual === 0 && ytd.ncfBudget === 0 ? (
              <Typography sx={{ fontSize: 16, color: "#999", lineHeight: 1 }}>
                -
              </Typography>
            ) : ytd.ncfActual >= ytd.ncfBudget ? (
              <TrendingUpIcon sx={{ fontSize: 16, color: "#2E7D32" }} />
            ) : (
              <TrendingDownIcon sx={{ fontSize: 16, color: "#e53935" }} />
            )
          }
          value={fmtZero(ytd.ncfActual)}
          valueColor={ytd.ncfActual >= 0 ? "#3E3E3E" : "#e53935"}
        />
        */}
      </Box>

      {/* コントロールバー (通常スクロール、隠れる) */}
      <Box
        sx={{
          mb: 1,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box sx={{ minWidth: 100 }}>
          <CustomSingleSelect
            label=""
            name="viewMode"
            control={control}
            errors={{}}
            inputWidth="100px"
            inputWidthSp="100%"
            options={VIEW_MODE_OPTIONS}
            onChangeValue={(v) => setViewMode(v as "monthly" | "quarterly")}
            hasSpacing={false}
          />
        </Box>
        <Box sx={{ flex: 1 }} />
        <Box data-testid="budget-csv-button">
          <Can action="budget:import">
            <CustomButton
              label="CSVアップロード"
              startIcon={<DescriptionIcon sx={{ fontSize: 18 }} />}
              onClick={() => setCsvModalOpen(true)}
            />
          </Can>
        </Box>
      </Box>

      {/* 予実テーブル */}
      <BudgetTableVirtual
        rowsVM={rowsVM}
        columns={displayColumns}
        loading={budgetTableLoading}
        stickyColWidth={vStickyWidth}
        onResizeSticky={setVStickyWidth}
        editingCellKey={editingCell}
        editingValue={editingValue}
        onStartEdit={vStartEdit}
        onCommitEdit={vCommitEdit}
        onCancelEdit={vCancelEdit}
        onChangeEditValue={vChangeEditValue}
        onToggle={vToggle}
        onOpenConflict={vOpenConflict}
        onLabelClick={vLabelClick}
      />

      {/* 新表示: 科目/カテゴリ追加モーダル */}
      <CustomModal
        title={
          vAddCategory?.level === "category" ? "カテゴリ追加" : "明細項目追加"
        }
        openModal={!!vAddCategory}
        handleCloseModal={() => setVAddCategory(null)}
        bodyText=""
        hideCloseButton
        modalType="deleteModal"
      >
        {vAddCategory && (
          <Box sx={{ mt: 1, minWidth: 280 }}>
            {/* 収益カテゴリ: 収入のみ。クリックしたサブグループで確定済なので disabled 表示 */}
            {vAddCategory.categoryType === "income" && (
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    mb: 0.5,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  収益カテゴリ
                  <Box component="span" sx={{ display: "flex", ml: 0.5 }}>
                    <RequireIcon />
                  </Box>
                </Typography>
                <Box
                  component="input"
                  value={
                    vAddCategory.revenueGroup === "stock_rent"
                      ? "貸室"
                      : vAddCategory.revenueGroup === "stock_other"
                        ? "付帯"
                        : vAddCategory.revenueGroup === "other"
                          ? "その他"
                          : (vAddCategory.revenueGroup ?? "")
                  }
                  disabled
                  sx={{
                    width: "100%",
                    height: 38,
                    px: 1.25,
                    fontSize: 13,
                    border: "1px solid #D9D9D9",
                    borderRadius: "8px",
                    outline: "none",
                    boxSizing: "border-box",
                    backgroundColor: "#F5F5F5",
                    color: "#999",
                    cursor: "not-allowed",
                  }}
                />
              </Box>
            )}
            {/* カテゴリ: 支出の明細科目追加時のみ。クリックしたサブグループ(expense_group)で
                確定済なので disabled 表示。カテゴリ追加モードでは非表示。 */}
            {vAddCategory.categoryType !== "income" &&
              vAddCategory.level !== "category" && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      mb: 0.5,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    カテゴリ
                    <Box component="span" sx={{ display: "flex", ml: 0.5 }}>
                      <RequireIcon />
                    </Box>
                  </Typography>
                  <Box
                    component="input"
                    value={vAddCategory.expenseGroup ?? ""}
                    disabled
                    sx={{
                      width: "100%",
                      height: 38,
                      px: 1.25,
                      fontSize: 13,
                      border: "1px solid #D9D9D9",
                      borderRadius: "8px",
                      outline: "none",
                      boxSizing: "border-box",
                      backgroundColor: "#F5F5F5",
                      color: "#999",
                      cursor: "not-allowed",
                    }}
                  />
                </Box>
              )}
            <Box sx={{ mb: 1.5 }}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  mb: 0.5,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {vAddCategory.level === "category" ? "カテゴリ名" : "明細項目"}
                <Box component="span" sx={{ display: "flex", ml: 0.5 }}>
                  <RequireIcon />
                </Box>
              </Typography>
              {/* プルダウンではなく、任意の項目名を打つだけのシンプルなフォーム (C3)。 */}
              <Box
                component="input"
                autoFocus
                value={vAddName}
                placeholder={
                  vAddCategory.level === "category"
                    ? "カテゴリ名を入力"
                    : "明細項目名を入力"
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setVAddName(e.target.value);
                  if (vAddError) setVAddError(null);
                }}
                sx={{
                  width: "100%",
                  height: 38,
                  px: 1.25,
                  fontSize: 13,
                  border: `1px solid ${vAddError ? "#e53935" : "#D9D9D9"}`,
                  borderRadius: "8px",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {vAddError && (
                <Typography sx={{ mt: 0.5, fontSize: 12, color: "#e53935" }}>
                  {vAddError}
                </Typography>
              )}
            </Box>
            <Box
              sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 1 }}
            >
              <CustomButton
                label="戻る"
                buttonCategory="cancelModal"
                onClick={() => {
                  setVAddCategory(null);
                  setVAddName("");
                  setVAddError(null);
                }}
              />
              <Can action="budget:edit">
                <CustomButton
                  label="登録"
                  buttonCategory="submit"
                  disabled={!vAddName.trim() || addCategorySubmitting}
                  onClick={() => {
                    const trimmed = vAddName.trim();
                    if (!trimmed || addCategorySubmitting) return;
                    // カテゴリ(親, level="category")追加モード
                    if (vAddCategory.level === "category") {
                      const dupCat = (categories.data as any[]).some(
                        (c) =>
                          !c.deleted_at &&
                          c.level === "category" &&
                          (c.category_type === "expense" ||
                            c.category_type === "expense_opex" ||
                            c.category_type === "expense_capex") &&
                          c.name === trimmed,
                      );
                      if (dupCat) {
                        setVAddError(`カテゴリ「${trimmed}」は既に存在します`);
                        return;
                      }
                      handleAddExpenseCategory(trimmed);
                      setVAddCategory(null);
                      setVAddName("");
                      setVAddError(null);
                      return;
                    }
                    // 同名チェック: 同 (category_type + revenue_group/expense_group + name)
                    // の科目が既に存在すればエラー。トーストではなく input 下にインライン (C3)。
                    const isIncome = vAddCategory.categoryType === "income";
                    const dup = (categories.data as any[]).some((c) => {
                      if (c.deleted_at) return false;
                      if (c.name !== trimmed) return false;
                      if (isIncome) {
                        return (
                          c.category_type === "income" &&
                          (c.revenue_group ?? undefined) ===
                            vAddCategory.revenueGroup
                        );
                      }
                      return (
                        (c.category_type === "expense" ||
                          c.category_type === "expense_opex" ||
                          c.category_type === "expense_capex") &&
                        (c.expense_group ?? undefined) ===
                          (vAddCategory.expenseGroup ?? undefined)
                      );
                    });
                    if (dup) {
                      setVAddError(`「${trimmed}」は既に存在します`);
                      return;
                    }
                    handleAddCategory(
                      vAddCategory.categoryType,
                      vAddCategory.revenueGroup as any,
                      trimmed,
                      vAddCategory.expenseGroup,
                    );
                    setVAddCategory(null);
                    setVAddName("");
                    setVAddError(null);
                  }}
                  sx={{ width: "50% !important" }}
                />
              </Can>
            </Box>
          </Box>
        )}
      </CustomModal>

      {/* 手動値と積み上げのバッティング選択モーダル (警告アイコン押下で開く) */}
      <CustomModal
        title="金額の選択"
        openModal={!!conflictModal}
        handleCloseModal={() => setConflictModal(null)}
        bodyText=""
        hideCloseButton
        modalType="deleteModal"
      >
        <Box>
          <Typography sx={{ fontSize: "14px", color: "#666" }}>
            {conflictModal
              ? (() => {
                  const rec = list.data.find(
                    (r) => r.sort_key === conflictModal.sortKey,
                  );
                  const catName = rec
                    ? (categoryNameById.get(rec.category_id) ?? rec.category)
                    : "";
                  const mk = orderedKeys[conflictModal.colIdx] || "";
                  const monthLabel = /^\d{4}-\d{2}$/.test(mk)
                    ? `${Number(mk.slice(5, 7))}月`
                    : "";
                  const fieldLabel =
                    conflictModal.field === "budget" ? "予算" : "実績";
                  if (conflictModal.detailLabel) {
                    const sourceLabel =
                      rec && isExpenseBudgetGroup(rec.category_type)
                        ? "支出実績"
                        : "入金合計";
                    return `「${conflictModal.detailLabel}」の手動入力と${sourceLabel}の数値が一致していません。反映する項目を選択してください。`;
                  }
                  return `「${catName}」${fieldLabel}${
                    monthLabel ? `(${monthLabel})` : ""
                  } の手動入力値と、明細(区画)の積み上げ合計が一致していません。反映する値を選んでください。`;
                })()
              : ""}
          </Typography>
          {conflictModal && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              {/* 手動入力値: ラベルは自前で上に出し、内蔵ラベルは隠す。
                  チェックボックスと input を中央揃えの行にして縦位置を合わせる。 */}
              <Box>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#3E3E3E",
                    mb: 0.5,
                    // チェックボックス + gap ぶん下げて input 開始位置に揃える。
                    pl: "40px",
                  }}
                >
                  {conflictModal.detailLabel
                    ? "手動入力（CSVアップロード含む）"
                    : "手動入力値"}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CustomCheckbox
                    checked={conflictModal.choice === "manual"}
                    onChange={() =>
                      setConflictModal({ ...conflictModal, choice: "manual" })
                    }
                    size="small"
                    boxSize={28}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      "& > *": { margin: "0 !important" },
                      "& .inputLabel": { display: "none" },
                    }}
                  >
                    <CustomTextInput
                      label=""
                      name="manualAmount"
                      register={conflictForm.register}
                      errors={conflictForm.formState.errors}
                      watch={conflictForm.watch}
                      setValue={conflictForm.setValue}
                      type="number"
                      showYen
                      inputWidth="100%"
                      hasSpacing={false}
                    />
                  </Box>
                </Box>
              </Box>
              {/* 積み上げ (明細合計) */}
              <Box>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#3E3E3E",
                    mb: 0.5,
                    // チェックボックス + gap ぶん下げて input 開始位置に揃える。
                    pl: "40px",
                  }}
                >
                  {conflictModal.detailLabel
                    ? (() => {
                        const rec = list.data.find(
                          (r) => r.sort_key === conflictModal.sortKey,
                        );
                        return rec && isExpenseBudgetGroup(rec.category_type)
                          ? "支出実績"
                          : "入金合計";
                      })()
                    : "積み上げ(明細合計)"}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CustomCheckbox
                    checked={conflictModal.choice === "accumulated"}
                    onChange={() =>
                      setConflictModal({
                        ...conflictModal,
                        choice: "accumulated",
                      })
                    }
                    size="small"
                    boxSize={28}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      "& > *": { margin: "0 !important" },
                      "& .inputLabel": { display: "none" },
                      // disabled でも背景がグレーにならないため明示
                      // (工事詳細の disabled と同じ #F5F5F5。文字もグレー)。
                      // 角丸(8px)を入力と合わせないとグレーが角からはみ出る。
                      "& .Mui-disabled": {
                        backgroundColor: "#F5F5F5 !important",
                        borderRadius: "8px",
                      },
                      // disabled はホバーしても背景・border を変えない (ホバー効果なし)。
                      "& .Mui-disabled:hover": {
                        backgroundColor: "#F5F5F5 !important",
                      },
                      "& .Mui-disabled .MuiOutlinedInput-notchedOutline, & .Mui-disabled:hover .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: "#D9D9D9 !important",
                        },
                      // disabled でも文字色は元の色 (primary #3E3E3E) のまま。
                      "& .MuiInputBase-input.Mui-disabled": {
                        color: "#3E3E3E",
                        WebkitTextFillColor: "#3E3E3E",
                      },
                    }}
                  >
                    <CustomTextInput
                      label=""
                      name="accumAmount"
                      register={conflictForm.register}
                      errors={conflictForm.formState.errors}
                      watch={conflictForm.watch}
                      setValue={conflictForm.setValue}
                      type="number"
                      showYen
                      // 積み上げ(自動集計)は編集不可。disabled でグレーアウト表示。
                      disabled
                      inputWidth="100%"
                      hasSpacing={false}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 1 }}>
          <CustomButton
            label="戻る"
            onClick={() => setConflictModal(null)}
            buttonCategory="cancelModal"
          />
          <Can action="budget:edit">
            <CustomButton
              label="反映"
              onClick={handleApplyConflict}
              buttonCategory="submit"
              sx={{ width: "50% !important", height: "36px !important" }}
            />
          </Can>
        </Box>
      </CustomModal>

      {/* 支出追加プレビュードロワー (Notion 風)
          ExpenseDetailPage (mode="new") を PreviewDrawer に埋め込み。
          予実画面から直接 ExpenseRecord を登録する。budget_category_id を紐付けて
          支払済で保存すれば postExpenseActual 経由で budget.actual に反映される。 */}
      <ExpenseCreateDrawer
        open={expenseDrawer.open}
        onClose={() => setExpenseDrawer((s) => ({ ...s, open: false }))}
        propertyId={propertyId}
        presetBudgetCategoryId={expenseDrawer.rowCategoryId}
        presetStatus="支払済"
        // 行グループから expense_type を自動付与
        presetExpenseType={
          expenseDrawer.rowGroup === "expense_capex"
            ? "CAPEX"
            : expenseDrawer.rowGroup === "expense_opex" ||
                expenseDrawer.rowGroup === "expense"
              ? "OPEX"
              : undefined
        }
        onCreated={() => {
          dispatch(
            fetchBudgets({
              property_id: propertyId,
              fiscal_year: String(fiscalYear),
            }) as any,
          );
        }}
      />

      {/* 支出詳細プレビュードロワー (Notion 風)
          支出行の内訳クリックで開く。detail.id = expense_id (BE で流用) */}
      <ExpenseDetailDrawer
        open={!!previewExpenseId}
        onClose={() => setPreviewExpenseId(null)}
        expenseId={previewExpenseId}
        propertyId={propertyId}
        propertyName={propertyName}
        onUpdated={() => {
          dispatch(
            fetchBudgets({
              property_id: propertyId,
              fiscal_year: String(fiscalYear),
            }) as any,
          );
        }}
      />

      {/* 区画プレビュー — 復元した PrivateAreaDetailDrawer (PrivateAreaDetail embedded) */}
      <PrivateAreaDetailDrawer
        open={!!previewSectionId}
        onClose={() => setPreviewSectionId(null)}
        propertyId={propertyId}
        privateAreaId={previewSectionId}
      />

      {/* 「収入追加」: Unit 登録モーダル (賃料/共益費は専有部固定、その他は未指定) */}
      <UnitCreateModal
        open={
          incomeAddModal.kind === "private_area" ||
          incomeAddModal.kind === "ancillary"
        }
        propertyId={propertyId}
        defaultAreaType={
          incomeAddModal.kind === "private_area" ? "private" : undefined
        }
        // 収益区分はどのカテゴリ配下から開いたかで確定しているので初期値で入れる。
        // 貸室 (賃料/共益費) → stock_rent、その他収入 → stock_other。
        defaultRevenueType={
          incomeAddModal.kind === "private_area"
            ? "stock_rent"
            : incomeAddModal.kind === "ancillary"
              ? "stock_other"
              : undefined
        }
        onClose={() => setIncomeAddModal({ kind: "none" })}
        onCreated={async () => {
          setIncomeAddModal({ kind: "none" });
          try {
            await dispatch(
              syncRentRoll({
                property_id: propertyId,
                fiscal_year: fiscalYear,
              }),
            ).unwrap();
          } catch {
            /* noop */
          }
          await dispatch(
            fetchBudgets({ property_id: propertyId, fiscal_year: fiscalYear }),
          );
        }}
      />

      {/* 「その他」収入の登録モーダル (Unit 非紐付け / 収入タブと共用)。
          初期カテゴリは「その他」固定。登録後は予実 actual を再取得。 */}
      <IncomeCreateDrawer
        open={incomePaymentOpen}
        onClose={() => setIncomePaymentOpen(false)}
        propertyId={propertyId}
        initialCategory="other"
        onCreated={() => {
          setIncomePaymentOpen(false);
          dispatch(
            fetchBudgets({ property_id: propertyId, fiscal_year: fiscalYear }),
          );
          // 「その他」入金行 (propertyList) も再取得して新規行を反映
          dispatch(fetchPaymentsByProperty({ propertyId }) as any);
        }}
      />

      {/* 「その他」入金行クリックで開く入金詳細プレビュードロワー (Notion 風)。
          Unit 非紐付けは propertySectionId を物件IDにフォールバック (収入一覧と同方針)。 */}
      <PaymentDetailDrawer
        open={!!incomeDetailPayment}
        onClose={() => setIncomeDetailPayment(null)}
        paymentId={incomeDetailPayment?.payment_id ?? null}
        propertySectionId={incomeDetailPayment?.unit_id || propertyId}
        propertyId={propertyId}
        propertyName={propertyName}
        fromSource="income"
        onUpdated={() => {
          dispatch(fetchPaymentsByProperty({ propertyId }) as any);
          dispatch(
            fetchBudgets({
              property_id: propertyId,
              fiscal_year: fiscalYear,
            }) as any,
          );
        }}
      />

      <CustomCsvUploadModal
        open={csvModalOpen}
        onClose={() => setCsvModalOpen(false)}
        title="予実データCSVアップロード"
        rules={[
          {
            title: "年度（必須）",
            description: "登録する年度を入力します。",
          },
          {
            title: "収入/支出（必須）",
            description: "「収入」または「支出」を入力します。",
          },
          {
            title: "カテゴリ（必須）",
            description:
              "予実管理にあるカテゴリを記載します。支出は未登録のカテゴリでも新規追加されます（収入は登録済のカテゴリのみ・未登録はエラー）。",
          },
          {
            title: "明細項目（必須）",
            description:
              "カテゴリ配下の明細科目名を入力します。支出は未登録の明細科目でも新規追加されます（収入は登録済の項目のみ・未登録はエラー）。",
          },
          {
            title: "予算/実績（必須）",
            description: "「予算」または「実績」を入力します。",
          },
          {
            title: "各月の金額（任意）",
            description:
              "全角半角どちらも入力できます。空欄は未入力扱いとなり、小数点以下は切り捨てます。複数月をまとめて入力できます。",
          },
        ]}
        onDownloadEmptyTemplate={handleDownloadEmptyTemplate}
        onDownloadCurrent={
          csvSerializableEntries.length > 0 ? handleDownloadCurrent : undefined
        }
        currentDataLabel={
          csvSerializableEntries.length > 0
            ? `${csvSerializableEntries.length}件の科目`
            : undefined
        }
        currentDataTitle={`現在のデータ (${fiscalYear}年度)`}
        onParseAndValidate={handleParseAndValidate}
        onConfirmUpload={handleConfirmCsvUpload}
        isSubmitting={csvSubmitting}
      />
    </Box>
  );
};

export default Budget;
